import React, { useState, useEffect, useMemo } from "react";
import {
  PlusCircle,
  Home,
  Car,
  User,
  Trash2,
  TrendingDown,
  Wallet,
  X,
  Hammer,
  DollarSign,
  Edit2,
  RefreshCw,
  Package,
  Coins,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  CheckSquare,
  Square,
  Info,
  Clock,
  Calendar,
  Moon,
  Sun,
  Crown,
  Check,
} from "lucide-react";

import "./styles.css";
import { Analytics } from "@vercel/analytics/react";
import * as Fathom from 'fathom-client';
import { supabase } from "./supabaseClient.ts";
import ProfilePage from './ProfilePage.tsx';
import Footer from './Footer.tsx';

// Transforme "1 000 000" en 1000000 (nombre)
const parseAmount = (val: string): number => {
  return Number(val.replace(/\s/g, ""));
};

// Transforme 1000000 en "1 000 000" (texte avec espaces)
const formatDisplay = (val: number | string): string => {
  if (!val && val !== 0) return "";
  const num = typeof val === "string" ? parseAmount(val) : val;
  return new Intl.NumberFormat("fr-FR").format(num);
};

// --- TYPES ---
interface Credit {
  id: string;
  nom: string;
  type: "immo" | "travaux" | "auto" | "perso";
  capital: number;
  taux: number;
  dureeAnnees: number;
  dateDebut: Date;
  userSessionId: string;
   differeMois?: number;
}

interface AmortizationRow {
  mois: number;
  date: string;
  dateObj: Date;
  capitalInitial: number;
  mensualite: number;
  capitalRembourse: number;
  interetsMensuels: number;
  interetsCumules: number;
  capitalRestant: number;
  interetsRestants: number;
}

type ViewMode =
  | "dashboard"
  | "renegociation"
  | "regroupement"
  | "remboursement"
  | "profile";

type BankName = "socredo" | "polynesie" | "tahiti";

// --- STYLES ---
const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(to bottom right, #e2e8f0, #cbd5e1)",
    padding: "2rem",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: { marginBottom: "2rem" },
  title: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: "0.5rem",
  },
  subtitle: { color: "#6b7280" },
  inlineAddButton: {
    backgroundColor: "#2563eb",
    color: "white",
    padding: "0.5rem 1rem",
    borderRadius: "0.5rem",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontWeight: "600",
    fontSize: "0.9rem",
    transition: "all 0.2s",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "1.5rem",
    marginBottom: "2rem",
  },
  kpiCard: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    padding: "1.5rem",
    borderLeft: "4px solid",
  },
  kpiCardBlue: { borderLeftColor: "#2563eb" },
  kpiCardOrange: { borderLeftColor: "#ea580c" },
  kpiCardGreen: { borderLeftColor: "#16a34a" },
  kpiHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "0.5rem",
  },
  kpiTitle: { fontSize: "0.875rem", fontWeight: "500", color: "#6b7280" },
  kpiValue: { fontSize: "1.875rem", fontWeight: "bold", color: "#1f2937" },
  creditGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: "1.5rem",
    marginBottom: "2rem",
  },
  creditCard: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    padding: "1.25rem",
    border: "1px solid #f3f4f6",
  },
  creditCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1rem",
  },
  creditCardIcon: {
    padding: "0.5rem",
    borderRadius: "0.5rem",
    color: "white",
    display: "inline-flex",
  },
  creditCardInfo: { display: "flex", alignItems: "center", gap: "0.75rem" },
  creditName: { fontWeight: "600", color: "#1f2937", margin: 0 },
  creditType: { fontSize: "0.875rem", color: "#6b7280", margin: 0 },
  actionButtons: { display: "flex", gap: "0.5rem" },
  actionButton: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    padding: "0.25rem",
  },
  creditStats: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "1rem",
  },
  creditStatsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "1rem",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: "#6b7280",
    margin: 0,
    marginBottom: "0.25rem",
  },
  statValue: {
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
    fontSize: "0.9rem",
  },
  progressBarContainer: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.25rem",
  },
  progressBarLabel: {
    fontSize: "0.75rem",
    color: "#6b7280",
    display: "flex",
    justifyContent: "space-between",
  },
  progressBarBg: {
    width: "100%",
    height: "12px",
    backgroundColor: "#e5e7eb",
    borderRadius: "4px",
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s ease",
  },
  progressBarDetails: {
    fontSize: "0.8rem",
    color: "#9ca3af",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "0.15rem",
  },
  optimizeSection: { marginBottom: "2rem" },
  optimizeTitle: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: "1rem",
  },
  optimizeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1.5rem",
  },
  optimizeCard: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    padding: "1.5rem",
    border: "2px solid transparent",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.75rem",
  },
  optimizeCardHeader: { display: "flex", alignItems: "center", gap: "0.75rem" },
  optimizeCardIcon: {
    padding: "0.75rem",
    borderRadius: "0.5rem",
    display: "inline-flex",
  },
  optimizeCardTitle: {
    fontSize: "1.125rem",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
  },
  optimizeCardDescription: {
    fontSize: "0.875rem",
    color: "#6b7280",
    margin: 0,
    lineHeight: "1.5",
  },
  modal: {
    position: "fixed" as const,
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: "1rem",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: "1rem",
    maxWidth: "28rem",
    width: "100%",
    padding: "1.5rem",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
  },
  modalTitle: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: "#1f2937",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    padding: "0.25rem",
  },
  form: { display: "flex", flexDirection: "column" as const, gap: "1rem" },
  formGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.5rem",
  },
  label: { fontSize: "0.875rem", fontWeight: "500", color: "#374151" },
  input: {
    width: "100%",
    padding: "0.5rem 1rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    boxSizing: "border-box" as const,
  },
  select: {
    width: "100%",
    padding: "0.5rem 1rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    boxSizing: "border-box" as const,
    backgroundColor: "white",
  },
  inputError: { borderColor: "#ef4444" },
  errorText: { fontSize: "0.75rem", color: "#ef4444", margin: 0 },
  typeSelector: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "0.5rem",
  },
  typeButton: {
    padding: "0.75rem",
    border: "2px solid",
    borderRadius: "0.5rem",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "0.5rem",
    cursor: "pointer",
    background: "white",
  },
  typeButtonActive: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
    color: "#2563eb",
  },
  typeButtonInactive: { borderColor: "#e5e7eb", color: "#6b7280" },
  buttonGroup: { display: "flex", gap: "0.75rem", paddingTop: "1rem" },
  button: {
    flex: 1,
    padding: "0.5rem 1rem",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    fontWeight: "500",
    cursor: "pointer",
    border: "none",
  },
  buttonSecondary: {
    backgroundColor: "white",
    border: "1px solid #d1d5db",
    color: "#374151",
  },
  buttonPrimary: { backgroundColor: "#2563eb", color: "white" },
  emptyState: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    padding: "3rem",
    textAlign: "center" as const,
  },
  table: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    padding: "1.5rem",
  },
  tableContainer: { overflowX: "auto" as const },
  tableElement: {
    width: "100%",
    fontSize: "0.875rem",
    borderCollapse: "collapse" as const,
  },
  th: {
    padding: "0.75rem 1rem",
    textAlign: "left" as const,
    fontWeight: "600",
    color: "#374151",
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
  },
  thRight: { textAlign: "right" as const },
  td: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid #f3f4f6",
    color: "#6b7280",
  },
  tdRight: { textAlign: "right" as const },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "1rem",
  },
  paginationButton: {
    padding: "0.5rem 1rem",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "none",
    borderRadius: "0.5rem",
    cursor: "pointer",
  },
  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 1rem",
    backgroundColor: "white",
    border: "1px solid #d1d5db",
    color: "#374151",
    borderRadius: "0.5rem",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "500",
    marginBottom: "1.5rem",
  },
  viewContainer: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  viewTitle: {
    fontSize: "1.875rem",
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: "0.5rem",
  },
  viewSubtitle: { fontSize: "1rem", color: "#6b7280", marginBottom: "2rem" },
  rangeContainer: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.5rem",
    marginBottom: "1.5rem",
  },
  rangeLabel: {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "500",
    color: "#374151",
  },
  rangeInput: { width: "100%", cursor: "pointer", accentColor: "#2563eb" },
  resultsCard: {
    backgroundColor: "#f9fafb",
    padding: "1.5rem",
    borderRadius: "0.75rem",
    marginTop: "2rem",
  },
  comparisonCol: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "1rem",
  },
  comparisonRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.5rem 0",
    borderBottom: "1px solid #e5e7eb",
  },
  bigMetric: { fontSize: "2rem", fontWeight: "bold", color: "#2563eb" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    fontSize: "0.75rem",
    fontWeight: "600",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
  },
  alertBox: {
    display: "flex",
    gap: "0.75rem",
    padding: "1rem",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "0.5rem",
    color: "#991b1b",
    fontSize: "0.875rem",
    marginTop: "1rem",
  },
  warningBox: {
    display: "flex",
    gap: "0.75rem",
    padding: "1rem",
    backgroundColor: "#fffbeb",
    border: "1px solid #fcd34d",
    borderRadius: "0.5rem",
    color: "#92400e",
    fontSize: "0.875rem",
    marginTop: "1rem",
  },
  successBox: {
    display: "flex",
    gap: "0.75rem",
    padding: "1rem",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "0.5rem",
    color: "#166534",
    fontSize: "0.875rem",
    marginTop: "1rem",
  },
  creditSelectCard: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "1rem",
    borderRadius: "0.5rem",
    border: "1px solid #e5e7eb",
    marginBottom: "0.75rem",
    cursor: "pointer",
    transition: "all 0.2s",
    backgroundColor: "white",
  },
  creditSelectCardActive: {
    backgroundColor: "#eff6ff",
    borderColor: "#2563eb",
  },
  bankCard: {
    border: "1px solid #e5e7eb",
    borderRadius: "0.5rem",
    padding: "1rem",
    cursor: "pointer",
    textAlign: "center" as const,
    transition: "all 0.2s",
  },
  bankCardActive: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
    color: "#2563eb",
    fontWeight: "bold",
  },
  summaryCard: {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    padding: "1rem",
    border: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    marginBottom: "1.5rem",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  summaryMetric: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "0.25rem",
  },
  comparisonGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "2rem",
  },
};

const MOIS = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

// --- UTILS ---
const calculerMensualite = (
  capital: number,
  tauxAnnuel: number,
  dureeAnnees: number
): number => {
  if (tauxAnnuel === 0 || dureeAnnees === 0) return 0;
  return Math.round(
    capital *
      (tauxAnnuel /
        12 /
        (Math.exp(dureeAnnees * 12 * Math.log(1 + tauxAnnuel / 12)) - 1) +
        tauxAnnuel / 12)
  );
};

// --- 1. Calculer la mensualité d'amortissement pur ---
const getMensualiteAmortissement = (credit: Credit): number => {
  // NOUVEAU : La durée d'amortissement est exactement la durée saisie (en années)
  const dureeAmortissementMois = credit.dureeAnnees * 12;
  return dureeAmortissementMois > 0
    ? calculerMensualite(
        credit.capital,
        credit.taux,
        dureeAmortissementMois / 12
      )
    : 0;
};

// --- 2. MODIFIÉ : Le moteur du tableau d'amortissement ---
const genererTableauAmortissement = (credit: Credit): AmortizationRow[] => {
  const tableau: AmortizationRow[] = [];
  const differe = credit.differeMois || 0;

  // NOUVEAU : La durée totale est la durée d'amortissement + le différé
  const dureeAmortissementMois = credit.dureeAnnees * 12;
  const dureeTotaleMois = dureeAmortissementMois + differe;

  const mensualiteAmortissement = getMensualiteAmortissement(credit);
  const dateDebut = new Date(credit.dateDebut);
  dateDebut.setDate(1);

  let capitalRestantDu = credit.capital;
  let interetsCumules = 0;
  let interetsTotaux = 0; // On va calculer les intérêts en direct

  // Période 1 : Le Différé (On ne paie QUE les intérêts)
  for (let mois = 0; mois < differe; mois++) {
    const dateMois = new Date(dateDebut);
    dateMois.setMonth(dateDebut.getMonth() + mois);
    const dateStr = dateMois.toLocaleDateString("fr-FR", {
      month: "short",
      year: "numeric",
    });

    const interetsMensuels = Math.round(capitalRestantDu * (credit.taux / 12));
    interetsCumules += interetsMensuels;
    interetsTotaux += interetsMensuels;

    tableau.push({
      mois: mois + 1,
      date: dateStr,
      dateObj: dateMois,
      capitalInitial: capitalRestantDu,
      mensualite: interetsMensuels, // Seulement les intérêts
      capitalRembourse: 0, // Aucun amortissement
      interetsMensuels,
      interetsCumules,
      capitalRestant: capitalRestantDu,
      interetsRestants: 0,
    });
  }

  // Période 2 : L'Amortissement classique
  for (let mois = differe; mois < dureeTotaleMois; mois++) {
    const dateMois = new Date(dateDebut);
    dateMois.setMonth(dateDebut.getMonth() + mois);
    const dateStr = dateMois.toLocaleDateString("fr-FR", {
      month: "short",
      year: "numeric",
    });

    const interetsMensuels = Math.round(capitalRestantDu * (credit.taux / 12));
    interetsCumules += interetsMensuels;
    interetsTotaux += interetsMensuels;

    let capitalRembourse = mensualiteAmortissement - interetsMensuels;
    if (capitalRestantDu - capitalRembourse < 0)
      capitalRembourse = capitalRestantDu;

    const nouveauCapitalRestantDu = Math.max(
      0,
      capitalRestantDu - capitalRembourse
    );

    tableau.push({
      mois: mois + 1,
      date: dateStr,
      dateObj: dateMois,
      capitalInitial: capitalRestantDu,
      mensualite: interetsMensuels + capitalRembourse,
      capitalRembourse,
      interetsMensuels,
      interetsCumules,
      capitalRestant: nouveauCapitalRestantDu,
      interetsRestants: 0,
    });
    capitalRestantDu = nouveauCapitalRestantDu;
    if (capitalRestantDu <= 0) break;
  }

  // Rétro-calcul des intérêts restants
  for (let i = 0; i < tableau.length; i++) {
    tableau[i].interetsRestants = Math.max(
      0,
      interetsTotaux - tableau[i].interetsCumules
    );
  }
  return tableau;
};

// --- 3. MODIFIÉ : Consolidation globale ---
const consoliderTableaux = (credits: Credit[]): AmortizationRow[] => {
  if (credits.length === 0) return [];
  const tableauxParCredit = credits.map((credit) => {
    const tab = genererTableauAmortissement(credit);
    const dDebut = new Date(credit.dateDebut);
    dDebut.setDate(1);
    return {
      credit,
      tableau: tab,
      startIndex: dDebut.getFullYear() * 12 + dDebut.getMonth(),
    };
  });

  const allStartIndexes = tableauxParCredit.map((t) => t.startIndex);
  const allEndIndexes = tableauxParCredit.map(
    (t) => t.startIndex + t.tableau.length
  );
  const minIndex = Math.min(...allStartIndexes);
  const maxIndex = Math.max(...allEndIndexes);
  const nbMoisTotal = maxIndex - minIndex;

  const tableauConsolide: AmortizationRow[] = [];
  let interetsCumulesTotal = 0;
  let interetsTotauxConsolides = 0;

  // Plus précis : on lit directement la fin des tableaux générés !
  tableauxParCredit.forEach(({ tableau }) => {
    if (tableau.length > 0) {
      interetsTotauxConsolides += tableau[tableau.length - 1].interetsCumules;
    }
  });

  for (let i = 0; i < nbMoisTotal; i++) {
    const currentIndex = minIndex + i;
    const currentYear = Math.floor(currentIndex / 12);
    const currentMonth = currentIndex % 12;
    const dateMois = new Date(currentYear, currentMonth, 1);
    const dateStr = dateMois.toLocaleDateString("fr-FR", {
      month: "short",
      year: "numeric",
    });

    let mensualiteTotal = 0;
    let capitalRembourseTotal = 0;
    let interetsMensuelsTotal = 0;
    let capitalRestantTotal = 0;

    tableauxParCredit.forEach(({ tableau, startIndex }) => {
      const localIndex = currentIndex - startIndex;
      if (localIndex >= 0 && localIndex < tableau.length) {
        const ligne = tableau[localIndex];
        mensualiteTotal += ligne.mensualite;
        capitalRembourseTotal += ligne.capitalRembourse;
        interetsMensuelsTotal += ligne.interetsMensuels;
        capitalRestantTotal += ligne.capitalRestant;
      }
    });

    interetsCumulesTotal += interetsMensuelsTotal;
    const interetsRestantsTotal = Math.max(
      0,
      interetsTotauxConsolides - interetsCumulesTotal
    );

    if (capitalRestantTotal > 0 || mensualiteTotal > 0) {
      tableauConsolide.push({
        mois: i + 1,
        date: dateStr,
        dateObj: dateMois,
        capitalInitial: 0,
        mensualite: mensualiteTotal,
        capitalRembourse: capitalRembourseTotal,
        interetsMensuels: interetsMensuelsTotal,
        interetsCumules: interetsCumulesTotal,
        capitalRestant: capitalRestantTotal,
        interetsRestants: interetsRestantsTotal,
      });
    }
  }
  return tableauConsolide;
};

// --- 4. Calcul des paliers pour la carte Avenir ---
const calculerPaliersMensualites = (credits: Credit[]) => {
  if (credits.length === 0) return [];
  const aujourdhui = new Date();
  aujourdhui.setDate(1);
  const datesCles: Date[] = [aujourdhui];

  // Trouver toutes les dates où la mensualité change
  credits.forEach((c) => {
    const debut = new Date(c.dateDebut);
    debut.setDate(1);
    const differe = c.differeMois || 0;

    // 1. Fin du différé (la mensualité va augmenter)
    if (differe > 0) {
      const finDiffere = new Date(debut);
      finDiffere.setMonth(debut.getMonth() + differe);
      if (finDiffere > aujourdhui) datesCles.push(finDiffere);
    }

    // 2. Fin totale du crédit (NOUVEAU : Durée + Différé)
    const fin = new Date(debut);
    fin.setMonth(debut.getMonth() + c.dureeAnnees * 12 + differe);
    if (fin > aujourdhui) datesCles.push(fin);
  });

  const datesClesUniques = datesCles
    .sort((a, b) => a.getTime() - b.getTime())
    .filter(
      (date, index, self) =>
        index ===
        self.findIndex(
          (d) =>
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
        )
    );

  const paliers = [];
  for (let i = 0; i < datesClesUniques.length - 1; i++) {
    const dateDebutPeriode = new Date(datesClesUniques[i]);
    const dateProchaineEtape = new Date(datesClesUniques[i + 1]);
    const dateFinPeriode = new Date(dateProchaineEtape);
    dateFinPeriode.setMonth(dateFinPeriode.getMonth() - 1);

    let mensualiteTotale = 0;
    credits.forEach((c) => {
      const debut = new Date(c.dateDebut);
      debut.setDate(1);
      const differe = c.differeMois || 0;
      const finDiffere = new Date(debut);
      finDiffere.setMonth(debut.getMonth() + differe);

      // NOUVEAU : Fin totale
      const fin = new Date(debut);
      fin.setMonth(debut.getMonth() + c.dureeAnnees * 12 + differe);

      if (fin > dateDebutPeriode && debut <= dateDebutPeriode) {
        if (differe > 0 && dateDebutPeriode < finDiffere) {
          mensualiteTotale += Math.round(c.capital * (c.taux / 12)); // Mensualité du différé
        } else {
          mensualiteTotale += getMensualiteAmortissement(c); // Mensualité normale
        }
      }
    });

    const formatMoisAnnee = (d: Date) =>
      `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    const debutStr = formatMoisAnnee(dateDebutPeriode);
    const finStr = formatMoisAnnee(dateFinPeriode);

    if (mensualiteTotale > 0) {
      paliers.push({
        periode: debutStr === finStr ? debutStr : `${debutStr} à ${finStr}`,
        mensualite: Math.round(mensualiteTotale),
      });
    }
  }
  return paliers;
};

const getDateReference = (): Date => {
  const aujourdhui = new Date();
  const moisPrecedent = new Date(aujourdhui);
  moisPrecedent.setMonth(aujourdhui.getMonth() - 1);
  return moisPrecedent;
};

const calculerInteretsRestantsAujourdhui = (credits: Credit[]): number => {
  const dateReference = getDateReference();
  let totalInteretsRestants = 0;
  credits.forEach((credit) => {
    const tableau = genererTableauAmortissement(credit);
    const dateDebut = new Date(credit.dateDebut);
    dateDebut.setDate(1);
    const refIndex =
      dateReference.getFullYear() * 12 + dateReference.getMonth();
    const debutIndex = dateDebut.getFullYear() * 12 + dateDebut.getMonth();
    const moisEcoules = refIndex - debutIndex + 1;
    const indexMoisActuel = Math.max(
      0,
      Math.min(moisEcoules - 1, tableau.length - 1)
    );
    if (tableau[indexMoisActuel])
      totalInteretsRestants += tableau[indexMoisActuel].interetsRestants;
  });
  return totalInteretsRestants;
};

const calculerDureeRestanteMois = (credit: Credit): number => {
  const dateReference = getDateReference();
  const dateDebut = new Date(credit.dateDebut);
  dateDebut.setDate(1);
  
  // NOUVEAU : La durée totale inclut maintenant le différé
  const dureeTotaleMois = credit.dureeAnnees * 12 + (credit.differeMois || 0);
  
  const refIndex = dateReference.getFullYear() * 12 + dateReference.getMonth();
  const debutIndex = dateDebut.getFullYear() * 12 + dateDebut.getMonth();
  const moisEcoules = refIndex - debutIndex + 1;
  return Math.max(0, dureeTotaleMois - moisEcoules);
};

const calculerCapitalRestantAujourdhui = (credit: Credit): number => {
  const dateReference = getDateReference();
  const dateDebut = new Date(credit.dateDebut);
  dateDebut.setDate(1);
  const tableau = genererTableauAmortissement(credit);
  const refIndex = dateReference.getFullYear() * 12 + dateReference.getMonth();
  const debutIndex = dateDebut.getFullYear() * 12 + dateDebut.getMonth();
  const moisEcoules = refIndex - debutIndex + 1;
  const indexMoisActuel = Math.max(
    0,
    Math.min(moisEcoules - 1, tableau.length - 1)
  );
  return tableau[indexMoisActuel]?.capitalRestant || credit.capital;
};

const calculerCapitalRestantTotalAujourdhui = (credits: Credit[]): number => {
  return credits.reduce(
    (total, credit) => total + calculerCapitalRestantAujourdhui(credit),
    0
  );
};

const getOrCreateSessionId = (): string => {
  let sessionId = localStorage.getItem("userSessionId");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem("userSessionId", sessionId);
  }
  return sessionId;
};

// --- LOGIQUE METIER : CALCUL DES FRAIS BANCAIRES ---
const calculerFraisNegociation = (
  bank: BankName,
  credit: Credit,
  capitalRestant: number,
  nouveauTaux: number,
  nouvelleDureeMois: number,
  dureeRestanteMois: number
): number => {
  const isImmo = credit.type === "immo";
  const isRateChange = Math.abs(credit.taux * 100 - nouveauTaux) > 0.01;
  const isExtension = nouvelleDureeMois > dureeRestanteMois;
  const isDurationChange = Math.abs(nouvelleDureeMois - dureeRestanteMois) > 1;

  if (bank === "polynesie") {
    if (isImmo) {
      if (isRateChange)
        return Math.max(101500, Math.round(credit.capital * 0.01));
      else if (isDurationChange) return 50750;
    } else {
      if (isRateChange || isDurationChange) return 51770;
    }
  } else if (bank === "socredo") {
    if (isImmo) {
      if (isRateChange)
        return Math.max(100000, Math.round(capitalRestant * 0.01));
      else if (isDurationChange) return 9600;
    } else {
      if (isRateChange) return 21715;
      if (isDurationChange) return 9600;
    }
  } else if (bank === "tahiti") {
    if (isImmo) {
      if (isRateChange)
        return Math.max(100000, Math.round(capitalRestant * 0.01));
      if (isExtension)
        return Math.max(25000, Math.round(capitalRestant * 0.01));
      if (isDurationChange) return 15077;
    } else {
      if (isExtension)
        return Math.max(25000, Math.round(capitalRestant * 0.01));
      if (isRateChange || isDurationChange) return 15077;
    }
  }
  return 0;
};

// --- COMPOSANTS EXISTANTS ---
const ProgressBar: React.FC<{
  label?: string;
  current: number; 
  total: number;
  remaining: number; 
  color: string;
  unit: "capital" | "duree" | "interets";
}> = ({ label, current, total, remaining, color, unit }) => {
  
  // NOUVEAU : VOCABULAIRE INVERSÉ (La couleur montre ce qui est payé)
  const getUnitLabel = (type: "highlighted" | "muted") => {
    if (unit === "capital") return type === "highlighted" ? "capital remboursé" : "restant dû";
    else if (unit === "duree") return type === "highlighted" ? "mois écoulés" : "mois restants";
    else return type === "highlighted" ? "intérêts payés" : "restants";
  };

  return (
    <div style={styles.progressBarContainer}>

      {/* INFOS AU-DESSUS DU GRAPHIQUE */}
      <div style={{ 
        ...styles.progressBarLabel, 
        justifyContent: "flex-start", 
        marginBottom: "0.35rem" 
      }}>
        <span style={{ fontWeight: "500", color: color, fontSize: "0.8rem" }}>
          {label ? `${label} : ` : ""} 
          {current.toLocaleString()} {getUnitLabel("highlighted")}
        </span>
      </div>

      {/* GRAPHIQUE À BARRE (Mathématiques sécurisées) */}
      <div style={{
        position: "relative",
        width: "100%",
        height: "1.25rem",
        backgroundColor: "#e5e7eb",
        borderRadius: "9999px",
        overflow: "hidden", 
      }}>
        {(() => {
          // Sécurisation stricte des valeurs
          const rawPercentage = total > 0 ? Math.min(100, Math.max(0, (current / total) * 100)) : 0;
          const val = Number(rawPercentage);
          const rem = 100 - val;
          
          return (
            <>
              {/* BARRE DE COULEUR (Fill) */}
              <div
                style={{
                  width: `${val}%`,
                  backgroundColor: color,
                  height: "100%",
                  transition: "width 0.5s ease-in-out",
                  borderRadius: "9999px"
                }}
              />

              {/* TEXTE PARTIE GAUCHE (Écoulé / Highlighted) */}
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  zIndex: 10,
                  whiteSpace: "nowrap",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  left: val > 12 ? `${val / 2}%` : `calc(${val}% + 0.5rem)`,
                  transform: val > 12 ? "translate(-50%, -50%)" : "translate(0, -50%)",
                  color: val > 12 ? "white" : color,
                }}
              >
                {val.toFixed(1)}%
              </span>

              {/* TEXTE PARTIE DROITE (Restant / Muted) */}
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  zIndex: 10,
                  whiteSpace: "nowrap",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  left: rem > 12 ? `${val + (rem / 2)}%` : `calc(${val}% - 0.5rem)`,
                  transform: rem > 12 ? "translate(-50%, -50%)" : "translate(-100%, -50%)",
                  color: rem > 12 ? "#6b7280" : "#4b5563",
                }}
              >
                {rem.toFixed(1)}%
              </span>
            </>
          );
        })()}
      </div>

      {/* INFOS SOUS LE GRAPHIQUE */}
      <div style={{ 
        ...styles.progressBarDetails, 
        justifyContent: "flex-end", 
        marginTop: "0.35rem" 
      }}>
        <span style={{ color: "#6b7280", fontWeight: "500", fontSize: "0.8rem" }}>
          {remaining.toLocaleString()} {getUnitLabel("muted")}
        </span>
      </div>
      
    </div>
  );
};

const CreditModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (credit: Omit<Credit, "id" | "userSessionId">) => void;
  existingCredit?: Credit | null;
  title: string;
}> = ({ isOpen, onClose, onSave, existingCredit, title }) => {
  const [formData, setFormData] = useState({
    nom: "",
    type: "immo" as "immo" | "travaux" | "auto" | "perso",
    capital: "",
    taux: "",
    dureeAnnees: "",
    mois: (new Date().getMonth() + 1).toString(),
    annee: new Date().getFullYear().toString(),
  });

  // États pour gérer l'option du différé
  const [hasDiffere, setHasDiffere] = useState(false);
  const [differeMois, setDiffereMois] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 30; i++) {
      years.push(currentYear - i);
    }
    return years;
  };
  const YEARS = generateYears();

  useEffect(() => {
    if (existingCredit) {
      const dateDebut = new Date(existingCredit.dateDebut);
      setFormData({
        nom: existingCredit.nom,
        type: existingCredit.type,
        capital: existingCredit.capital.toString(),
        taux: (existingCredit.taux * 100).toString(),
        dureeAnnees: existingCredit.dureeAnnees.toString(),
        mois: (dateDebut.getMonth() + 1).toString(),
        annee: dateDebut.getFullYear().toString(),
      });
      // Restaurer le différé s'il y en avait un
      if (existingCredit.differeMois && existingCredit.differeMois > 0) {
        setHasDiffere(true);
        setDiffereMois(existingCredit.differeMois.toString());
      } else {
        setHasDiffere(false);
        setDiffereMois("");
      }
    } else {
      setFormData({
        nom: "",
        type: "immo",
        capital: "",
        taux: "",
        dureeAnnees: "",
        mois: (new Date().getMonth() + 1).toString(),
        annee: new Date().getFullYear().toString(),
      });
      setHasDiffere(false);
      setDiffereMois("");
    }
  }, [existingCredit, isOpen]);

  if (!isOpen) return null;

  // Fonction utilitaire pour formater le millier (ex: 1 000 000)
  const formatDisplay = (val: string) => {
    if (!val) return "";
    const number = parseInt(val.replace(/\D/g, ""), 10);
    return isNaN(number) ? "" : number.toLocaleString("fr-FR");
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nom.trim()) newErrors.nom = "Le nom est requis";
    if (!formData.capital || parseFloat(formData.capital) <= 0)
      newErrors.capital = "Le capital doit être positif";
    if (
      !formData.taux ||
      parseFloat(formData.taux) < 0 ||
      parseFloat(formData.taux) > 15
    )
      newErrors.taux = "Le taux doit être entre 0 et 15%";
    if (
      !formData.dureeAnnees ||
      parseFloat(formData.dureeAnnees) < 1 ||
      parseFloat(formData.dureeAnnees) > 40
    )
      newErrors.dureeAnnees = "La durée doit être entre 1 et 40 ans";

    // Validation du différé
    if (hasDiffere) {
      const diff = parseInt(differeMois);
      if (
        isNaN(diff) ||
        diff < 1 ||
        diff >= parseFloat(formData.dureeAnnees) * 12
      ) {
        newErrors.differeMois =
          "Doit être valide et inférieur à la durée totale du prêt";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const dateDebut = new Date(
        parseInt(formData.annee),
        parseInt(formData.mois) - 1,
        1
      );
      onSave({
        nom: formData.nom,
        type: formData.type,
        capital: parseFloat(formData.capital.replace(/\D/g, "")), // Nettoie les espaces avant sauvegarde
        taux: parseFloat(formData.taux) / 100,
        dureeAnnees: parseFloat(formData.dureeAnnees),
        dateDebut: dateDebut,
        // Sauvegarde de l'option différé
        differeMois: hasDiffere ? parseInt(differeMois) : 0,
      });
      onClose();
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{title}</h2>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Type de crédit</label>
            <div style={styles.typeSelector}>
              {[
                { type: "immo" as const, icon: Home, label: "Immobilier" },
                { type: "travaux" as const, icon: Hammer, label: "Travaux" },
                { type: "auto" as const, icon: Car, label: "Auto" },
                { type: "perso" as const, icon: User, label: "Perso" },
              ].map(({ type, icon: Icon, label }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData({ ...formData, type })}
                  style={{
                    ...styles.typeButton,
                    ...(formData.type === type
                      ? styles.typeButtonActive
                      : styles.typeButtonInactive),
                  }}
                >
                  <Icon size={20} />
                  <span style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Nom du crédit</label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) =>
                setFormData({ ...formData, nom: e.target.value })
              }
              style={{
                ...styles.input,
                ...(errors.nom ? styles.inputError : {}),
              }}
              placeholder="Ex: Maison, Bungalow, AIM..."
            />
            {errors.nom && <p style={styles.errorText}>{errors.nom}</p>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Capital</label>
            <input
              type="text"
              inputMode="numeric"
              value={formatDisplay(formData.capital)}
              onChange={(e) => {
                const rawValue = e.target.value.replace(/\D/g, "");
                setFormData({ ...formData, capital: String(rawValue) });
              }}
              style={{
                ...styles.input,
                ...(errors.capital ? styles.inputError : {}),
              }}
            />
            {errors.capital && <p style={styles.errorText}>{errors.capital}</p>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Taux (%)</label>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={formData.taux}
              onChange={(e) =>
                setFormData({ ...formData, taux: e.target.value })
              }
              style={{
                ...styles.input,
                ...(errors.taux ? styles.inputError : {}),
              }}
              placeholder="3.1"
            />
            {errors.taux && <p style={styles.errorText}>{errors.taux}</p>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Durée (années)</label>
            <input
              type="number"
              inputMode="numeric"
              value={formData.dureeAnnees}
              onChange={(e) =>
                setFormData({ ...formData, dureeAnnees: e.target.value })
              }
              style={{
                ...styles.input,
                ...(errors.dureeAnnees ? styles.inputError : {}),
              }}
            />
            {errors.dureeAnnees && (
              <p style={styles.errorText}>{errors.dureeAnnees}</p>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Date de début</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <select
                value={formData.mois}
                onChange={(e) =>
                  setFormData({ ...formData, mois: e.target.value })
                }
                style={{
                  ...styles.select,
                  flex: 1,
                  ...(errors.mois ? styles.inputError : {}),
                }}
              >
                {MOIS.map((mois) => (
                  <option key={mois.value} value={mois.value}>
                    {mois.label}
                  </option>
                ))}
              </select>

              <select
                value={formData.annee}
                onChange={(e) =>
                  setFormData({ ...formData, annee: e.target.value })
                }
                style={{
                  ...styles.select,
                  flex: 1,
                  ...(errors.annee ? styles.inputError : {}),
                }}
              >
                {YEARS.map((year) => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* --- L'OPTION DIFFÉRÉ (Ajoutée juste ici en bas) --- */}
          <div style={styles.formGroup}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                marginTop: "0.5rem",
              }}
            >
              <input
                type="checkbox"
                checked={hasDiffere}
                onChange={(e) => setHasDiffere(e.target.checked)}
                style={{
                  cursor: "pointer",
                  accentColor: "#2563eb",
                  width: "16px",
                  height: "16px",
                }}
              />
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Avec différé
              </span>
            </label>

            {hasDiffere && (
              <div
                style={{
                  marginTop: "0.25rem",
                  padding: "0.75rem",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "0.5rem",
                  border: "1px dashed #d1d5db",
                }}
              >
                <label
                  style={{
                    fontSize: "0.8rem",
                    color: "#4b5563",
                    display: "block",
                    marginBottom: "0.5rem",
                  }}
                >
                  Durée du différé :
                </label>
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="number"
                    inputMode="numeric"
                    value={differeMois}
                    onChange={(e) => setDiffereMois(e.target.value)}
                    style={{
                      ...styles.input,
                      width: "100%",
                      paddingRight: "3.5rem",
                      ...(errors.differeMois ? styles.inputError : {}),
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: "1rem",
                      color: "#6b7280",
                      fontSize: "0.875rem",
                    }}
                  >
                    mois
                  </span>
                </div>
                {errors.differeMois && (
                  <p style={{ ...styles.errorText, marginTop: "0.25rem" }}>
                    {errors.differeMois}
                  </p>
                )}
              </div>
            )}
          </div>
          {/* ---------------------------------------------------- */}

          <div style={{ ...styles.buttonGroup, marginTop: "0.5rem" }}>
            <button
              type="button"
              onClick={onClose}
              style={{ ...styles.button, ...styles.buttonSecondary }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{ ...styles.button, ...styles.buttonPrimary }}
            >
              {existingCredit ? "Modifier" : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreditCard: React.FC<{
  credit: Credit;
  onDelete: (id: string) => void;
  onEdit: (credit: Credit) => void;
}> = ({ credit, onDelete, onEdit }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Détecter si on est sur mobile pour toujours afficher les boutons
  const isMobile = window.innerWidth < 768;
  const showButtons = isMobile || isHovered;

  const typeConfig = {
    immo: { icon: Home, color: "#3b82f6", label: "Immobilier" },
    travaux: { icon: Hammer, color: "#f59e0b", label: "Travaux" },
    auto: { icon: Car, color: "#10b981", label: "Auto" },
    perso: { icon: User, color: "#a855f7", label: "Perso" },
  };
  const config = typeConfig[credit.type];
  const Icon = config.icon;

  // 1. On génère d'abord le tableau pour savoir exactement où l'on en est
  const tableau = genererTableauAmortissement(credit);
  const dateReference = getDateReference();
  const dateDebut = new Date(credit.dateDebut);
  dateDebut.setDate(1);

  const refIndex = dateReference.getFullYear() * 12 + dateReference.getMonth();
  const debutIndex = dateDebut.getFullYear() * 12 + dateDebut.getMonth();
  const moisEcoules = refIndex - debutIndex + 1;
  const indexMoisActuel = Math.max(
    0,
    Math.min(moisEcoules - 1, tableau.length - 1)
  );

  // --- NOUVEAU : Logique d'affichage dynamique de la mensualité (Différé) ---
  const differeMois = credit.differeMois || 0;
  const isEnDiffere = indexMoisActuel < differeMois;
  const mensualiteActuelle =
    tableau[indexMoisActuel]?.mensualite || getMensualiteAmortissement(credit);
  // -------------------------------------------------------------------------

  const dureeRestanteMois = calculerDureeRestanteMois(credit);
  const dureeTotaleMois = credit.dureeAnnees * 12 + differeMois;
  const capitalRestantDu = calculerCapitalRestantAujourdhui(credit);

  // --- NOUVEAU : Lecture exacte des intérêts dans le tableau ---
  const interetsTotaux =
    tableau.length > 0 ? tableau[tableau.length - 1].interetsCumules : 0;
  const interetsRestants =
    tableau[indexMoisActuel]?.interetsRestants || interetsTotaux;
  // -------------------------------------------------------------

  const capitalRembourse = credit.capital - capitalRestantDu;
  const dureeEcoulee = dureeTotaleMois - dureeRestanteMois;
  const interetsPayes = interetsTotaux - interetsRestants;

  const dateFin = new Date(dateDebut);
  dateFin.setMonth(dateDebut.getMonth() + dureeTotaleMois - 1);
  const dateDebutStr = dateDebut.toLocaleDateString("fr-FR", {
    month: "short",
    year: "numeric",
  });
  const dateFinStr = dateFin.toLocaleDateString("fr-FR", {
    month: "short",
    year: "numeric",
  });

  return (
    <div
      style={{
        ...styles.creditCard,
        boxShadow: isHovered
          ? "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
          : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.creditCardHeader}>
        <div style={styles.creditCardInfo}>
          <div
            style={{ ...styles.creditCardIcon, backgroundColor: config.color }}
          >
            <Icon size={20} />
          </div>
          <div>
            <h3 style={styles.creditName}>{credit.nom}</h3>
            <p style={styles.creditType}>{config.label}</p>
          </div>
        </div>
        <div style={styles.actionButtons}>
          <button
            onClick={() => onEdit(credit)}
            style={{
              ...styles.actionButton,
              opacity: showButtons ? 1 : 0,
              color: showButtons ? "#2563eb" : "#9ca3af",
            }}
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => onDelete(credit.id)}
            style={{
              ...styles.actionButton,
              opacity: showButtons ? 1 : 0,
              color: showButtons ? "#ef4444" : "#9ca3af",
            }}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      <div style={styles.creditStats}>
        <div style={styles.creditStatsRow}>
          <div>
            <p style={styles.statLabel}>Capital</p>
            <p style={styles.statValue}>
              {credit.capital.toLocaleString()} XPF
            </p>
          </div>
          <div>
            <p style={styles.statLabel}>Taux</p>
            <p style={styles.statValue}>{(credit.taux * 100).toFixed(2)}%</p>
          </div>
          <div>
            {/* --- NOUVEAU : Affichage de la mention (Différé) si nécessaire --- */}
            <p style={styles.statLabel}>
              Mensualité{" "}
              {isEnDiffere && (
                <span
                  style={{
                    fontStyle: "italic",
                    color: "#f59e0b",
                    fontSize: "0.7rem",
                  }}
                >
                  <br />
                  (Différé)
                </span>
              )}
            </p>
            <p style={{ ...styles.statValue, color: "#2563eb" }}>
              {mensualiteActuelle.toLocaleString()} XPF
            </p>
            {/* ---------------------------------------------------------------- */}
          </div>
        </div>

        <ProgressBar
          current={capitalRembourse}
          total={credit.capital}
          remaining={capitalRestantDu}
          color="#10b981"
          unit="capital"
        />

        <div style={{ marginTop: "0.5rem", marginBottom: "0.25rem" }}>
          <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: 0 }}>
            Durée : {credit.dureeAnnees} ans ({dateDebutStr} à {dateFinStr})
          </p>
        </div>

        <ProgressBar
          current={dureeEcoulee}
          total={dureeTotaleMois}
          remaining={dureeRestanteMois}
          color="#3b82f6"
          unit="duree"
        />

        <div style={{ marginTop: "0.5rem", marginBottom: "0.25rem" }}>
          <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: 0 }}>
            Total intérêts : {interetsTotaux.toLocaleString()} XPF
          </p>
        </div>

        <ProgressBar
          current={interetsPayes}
          total={interetsTotaux}
          remaining={interetsRestants}
          color="#f59e0b"
          unit="interets"
        />
      </div>
    </div>
  );
};

const OptimizeSection: React.FC<{ onSelectMode: (mode: ViewMode) => void }> = ({
  onSelectMode,
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const options = [
    {
      id: "renegociation",
      mode: "renegociation" as ViewMode,
      icon: RefreshCw,
      color: "#2563eb",
      title: "Renégocier",
      description: "Réduire le taux ou la durée d'un crédit existant",
    },
    {
      id: "regroupement",
      mode: "regroupement" as ViewMode,
      icon: Package,
      color: "#7c3aed",
      title: "Racheter/Regrouper",
      description: "Restructurer vos crédits",
    },
    {
      id: "remboursement",
      mode: "remboursement" as ViewMode,
      icon: Coins,
      color: "#16a34a",
      title: "Remboursement anticipé",
      description: "Simuler un remboursement partiel ou total",
    },
  ];
  return (
    <div style={styles.optimizeSection}>
      <h2 style={styles.optimizeTitle}>Optimiser mes crédits</h2>
      <div style={styles.optimizeGrid}>
        {options.map((option) => {
          const IconComponent = option.icon;
          const isHovered = hoveredCard === option.id;
          return (
            <div
              key={option.id}
              style={{
                ...styles.optimizeCard,
                borderColor: isHovered ? option.color : "transparent",
                transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                boxShadow: isHovered
                  ? "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                  : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              }}
              onClick={() => {
                onSelectMode(option.mode); // 1. Ouvre la page de l'outil
                Fathom.trackEvent(`Clic Outil ${option.title}`); // 2. Envoie l'event Fathom avec le titre de la carte !
              }}
              onMouseEnter={() => setHoveredCard(option.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {" "}
              <div style={styles.optimizeCardHeader}>
                {" "}
                <div
                  style={{
                    ...styles.optimizeCardIcon,
                    backgroundColor: `${option.color}15`,
                    color: option.color,
                  }}
                >
                  {" "}
                  <IconComponent size={24} />{" "}
                </div>{" "}
                <h3 style={styles.optimizeCardTitle}>{option.title}</h3>{" "}
              </div>{" "}
              <p style={styles.optimizeCardDescription}>{option.description}</p>{" "}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// RENEGOCIATION VIEW
// ------------------------------------------------------------------
const RenegociationView: React.FC<{
  credits: Credit[];
  onBack: () => void;
}> = ({ credits, onBack }) => {
  const [selectedCreditId, setSelectedCreditId] = useState<string>(
    credits[0]?.id || ""
  );
  const [nouveauTaux, setNouveauTaux] = useState<number>(3.5);
  const [nouvelleDureeMois, setNouvelleDureeMois] = useState<number>(180);
  const [selectedBank, setSelectedBank] = useState<BankName | null>(null);

  // Force le scroll en haut de page à l'ouverture du composant
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // -------------------------

  const selectedCredit = credits.find((c) => c.id === selectedCreditId);

  useEffect(() => {
    if (selectedCredit) {
      setNouveauTaux(selectedCredit.taux * 100);
      const dureeRestante = calculerDureeRestanteMois(selectedCredit);
      setNouvelleDureeMois(dureeRestante);
    }
  }, [selectedCreditId]);

  if (!selectedCredit) return null;

  // --- CALCULS ---
  const capitalRestantDu = calculerCapitalRestantAujourdhui(selectedCredit);
  const mensualiteActuelle = calculerMensualite(
    selectedCredit.capital,
    selectedCredit.taux,
    selectedCredit.dureeAnnees
  );
  const dureeRestanteMois = calculerDureeRestanteMois(selectedCredit);
  const interetsRestantsActuels = calculerInteretsRestantsAujourdhui([
    selectedCredit,
  ]);

  const nouvelleMensualite = calculerMensualite(
    capitalRestantDu,
    nouveauTaux / 100,
    nouvelleDureeMois / 12
  );
  const nouveauxInteretsTotaux =
    nouvelleMensualite * nouvelleDureeMois - capitalRestantDu;

  // 1. DÉTECTION DE MODIFICATION
  // On compare les sliders avec les valeurs d'origine du crédit
  const isModified =
    Math.abs(nouveauTaux - selectedCredit.taux * 100) > 0.01 ||
    nouvelleDureeMois !== dureeRestanteMois;

  // 2. FORCER À 0 SI PAS DE MODIFICATION
  const gainInterets = isModified
    ? interetsRestantsActuels - nouveauxInteretsTotaux
    : 0;
  const gainMensuel = isModified ? mensualiteActuelle - nouvelleMensualite : 0;

  const fraisNego = selectedBank
    ? calculerFraisNegociation(
        selectedBank,
        selectedCredit,
        capitalRestantDu,
        nouveauTaux,
        nouvelleDureeMois,
        dureeRestanteMois
      )
    : 0;

  const gainNet = isModified ? gainInterets - fraisNego : 0;

  // --- FONCTION VERDICT ---
  const getVerdict = () => {
    if (gainInterets > 0 && gainNet < 0) {
      return {
        type: "warning",
        title: "Attention aux frais",
        text: `Sur le papier, vous économisez ${gainInterets.toLocaleString()} XPF d'intérêts. Cependant, les frais de négociation (${fraisNego.toLocaleString()} XPF) sont supérieurs à ce gain. L'opération vous coûte réellement ${Math.abs(
          gainNet
        ).toLocaleString()} XPF.`,
      };
    }
    if (gainMensuel >= 0 && gainNet >= 0) {
      return {
        type: "success",
        title: "Opération Rentable",
        text: `Excellent ! Vous gagnez sur les 2 tableaux : une mensualité plus faible et une économie des intérêts de ${gainNet.toLocaleString()} XPF. Vous pouvez encore optimiser le gain d'intérêts en réduisant la durée de votre crédit (votre mensualité augmentera). `,
      };
    }
    if (gainMensuel > 0 && gainNet < 0) {
      const dureePlus = nouvelleDureeMois - dureeRestanteMois;
      return {
        type: "warning",
        title: "Gain de Cashflow",
        text: `Intéressant pour alléger votre budget mensuel. Attention, en allongeant la durée (+${Math.round(
          dureePlus
        )} mois), vous augmentez le coût total (intérêts) de ${Math.abs(
          gainNet
        ).toLocaleString()} XPF.`,
      };
    }
    if (gainMensuel < 0 && gainNet > 0) {
      const dureeMoins = dureeRestanteMois - nouvelleDureeMois;
      return {
        type: "warning",
        title: "Économie d'intérêts mais baisse du cashflow",
        text: `Votre mensualité augmente, mais en réduisant la durée (-${Math.round(
          dureeMoins
        )} mois), vous réalisez une économie d'intérêts de ${gainNet.toLocaleString()} XPF.`,
      };
    }
    return {
      type: "danger",
      title: "Non Rentable",
      text: `Cette opération n'est pas recommandée. Vous augmentez à la fois votre mensualité et le coût total du crédit.`,
    };
  };

  const verdict = getVerdict();

  return (
    <div className="view-container" style={styles.viewContainer}>
      <button style={styles.backButton} onClick={onBack}>
        ← Retour au dashboard
      </button>
      <h1 style={styles.viewTitle}>Renégocier un crédit</h1>
      <p style={styles.viewSubtitle}>
        Simulez une modification de taux ou de durée de votre crédit.
      </p>

      <div style={{ marginBottom: "2rem" }}>
        <label style={styles.label}>Sélectionnez un crédit</label>
        <select
          style={{
            ...styles.select,
            marginTop: "0.5rem",
            maxWidth: "100%",
            width: "400px",
          }}
          value={selectedCreditId}
          onChange={(e) => setSelectedCreditId(e.target.value)}
        >
          {credits.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nom} ({c.capital.toLocaleString()} XPF)
            </option>
          ))}
        </select>
      </div>

      {/* --- BLOC RÉSUMÉ CRÉDIT --- */}
      <div className="summary-grid">
        <div className="summary-item">
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>CRD</span>
          <span
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              color: "#1f2937",
            }}
          >
            {capitalRestantDu.toLocaleString()} XPF
          </span>
        </div>

        <div className="summary-item">
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
            Durée restante
          </span>
          <span
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              color: "#1f2937",
            }}
          >
            {dureeRestanteMois} mois (
            {(dureeRestanteMois / 12).toFixed(1)} ans)
          </span>
        </div>

        <div className="summary-item">
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
            Taux du crédit
          </span>
          <span
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              color: "#1f2937",
            }}
          >
            {(selectedCredit.taux * 100).toFixed(2)}%
          </span>
        </div>
      </div>

      {/* --- GRILLE PRINCIPALE --- */}
      <div className="main-grid">
        {/* COLONNE GAUCHE : Paramètres */}
        <div>
          <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              marginBottom: "1.5rem",
              color: "#1f2937",
            }}
          >
            Nouveaux paramètres
          </h3>

          <div style={styles.rangeContainer}>
            <div style={styles.rangeLabel}>
              <span>Nouveau taux</span>
              <span style={{ color: "#2563eb" }}>
                {nouveauTaux.toFixed(2)}%
              </span>
            </div>
            <div className="input-row">
              <input
                type="range"
                min="0.5"
                max="10"
                step="0.05"
                value={nouveauTaux.toFixed(2)}
                onChange={(e) => setNouveauTaux(parseFloat(e.target.value))}
                style={styles.rangeInput}
              />
              <input
                type="number"
                step="0.05"
                value={nouveauTaux}
                onChange={(e) => setNouveauTaux(parseFloat(e.target.value))}
                style={{ ...styles.input, width: "90px" }}
              />
            </div>
          </div>

          {/* NOUVELLE DURÉE (Saisie en mois avec boutons -/+) */}
          <div style={{ ...styles.rangeContainer, display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={styles.rangeLabel}>
              <span>Nouvelle durée</span>
              <span style={{ color: "#2563eb", fontWeight: "bold" }}>
                {nouvelleDureeMois} mois ({(nouvelleDureeMois / 12).toFixed(1)} ans)
              </span>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
              {/* Bouton MOINS 1 mois */}
              <button
                onClick={() => setNouvelleDureeMois(Math.max(12, nouvelleDureeMois - 1))}
                style={{
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  width: "40px",
                  height: "40px",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151"
                }}
              >
                -
              </button>
              
              {/* Saisie directe des MOIS */}
              <div style={{ position: "relative", flexGrow: 1, display: "flex", alignItems: "center" }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min="12"
                  max="300"
                  value={nouvelleDureeMois}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    // On sécurise la saisie entre 12 mois (1 an) et 300 mois (25 ans)
                    if (!isNaN(val)) setNouvelleDureeMois(Math.min(300, Math.max(12, val)));
                  }}
                  style={{ ...styles.input, width: "100%", textAlign: "center", fontSize: "1.1rem", paddingRight: "3.5rem" }}
                />
                <span style={{ position: "absolute", right: "1rem", color: "#6b7280", fontSize: "0.9rem" }}>mois</span>
              </div>
              
              {/* Bouton PLUS 1 mois */}
              <button
                onClick={() => setNouvelleDureeMois(Math.min(300, nouvelleDureeMois + 1))}
                style={{
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  width: "40px",
                  height: "40px",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151"
                }}
              >
                +
              </button>
            </div>
            <p style={{ fontSize: "0.75rem", color: "#9ca3af", textAlign: "center", margin: 0 }}>
              Astuce : Utilisez les boutons -/+ pour ajuster au mois près.
            </p>
          </div>

          <div style={{ marginTop: "2rem" }}>
            <h3
              style={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                color: "#374151",
                marginTop: "1.5rem",
                marginBottom: "0.75rem",
              }}
            >
              Frais de négociation
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
                gap: "0.5rem",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  ...styles.bankCard,
                  ...(selectedBank === "socredo" ? styles.bankCardActive : {}),
                }}
                onClick={() => setSelectedBank("socredo")}
              >
                Socredo
              </div>
              <div
                style={{
                  ...styles.bankCard,
                  ...(selectedBank === "polynesie" ? styles.bankCardActive : {}),
                }}
                onClick={() => setSelectedBank("polynesie")}
              >
                BP
              </div>
              <div
                style={{
                  ...styles.bankCard,
                  ...(selectedBank === "tahiti" ? styles.bankCardActive : {}),
                }}
                onClick={() => setSelectedBank("tahiti")}
              >
                BT
              </div>
            </div>

            {selectedBank && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.75rem",
                    backgroundColor: "#fff7ed",
                    borderRadius: "0.5rem",
                    border: "1px solid #fed7aa",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.9rem",
                      color: "#9a3412",
                      fontWeight: "500",
                    }}
                  >
                    Frais estimés :
                  </span>
                  <span
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color: "#c2410c",
                    }}
                  >
                    {Math.round(fraisNego).toLocaleString()} XPF
                  </span>
                </div>
                
                {/* NOUVEAU : Message de précision sur les tarifs */}
                <p style={{ 
                  fontSize: "0.75rem", 
                  color: "#6b7280", 
                  margin: 0, 
                  textAlign: "right",
                  fontStyle: "italic" 
                }}>
                  * Calculé sur la base des tarifs bancaires en vigueur.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* COLONNE DROITE : Résultats */}
        <div style={styles.resultsCard}>
          <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              marginBottom: "1.5rem",
              color: "#1f2937",
            }}
          >
            Comparaison
          </h3>

          <div
            style={{
              marginBottom: "1.5rem",
              paddingBottom: "1rem",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <p
              style={{
                fontSize: "0.9rem",
                color: "#6b7280",
                margin: "0 0 0.5rem 0",
              }}
            >
              Mensualité
            </p>

            <div className="comparison-row">
              <span
                style={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "#9ca3af",
                  textDecoration: "line-through",
                }}
              >
                {mensualiteActuelle.toLocaleString()} XPF
              </span>

              <ArrowRight
                size={20}
                strokeWidth={3}
                color="#6b7280"
                className="arrow-icon"
              />

              <span
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: isModified ? "#2563eb" : "#9ca3af",
                }}
              >
                {/* On affiche la nouvelle mensualité, ou l'ancienne si non modifié pour éviter les écarts d'arrondi */}
                {(isModified
                  ? nouvelleMensualite
                  : mensualiteActuelle
                ).toLocaleString()}{" "}
                XPF
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                marginTop: "0.5rem",
              }}
            >
              {gainMensuel > 0 ? (
                <div style={styles.badge}>
                  <TrendingDown size={14} />
                  <span>Gain mensuel : {gainMensuel.toLocaleString()} XPF</span>
                </div>
              ) : (
                <div
                  style={{
                    ...styles.badge,
                    backgroundColor: isModified ? "#fee2e2" : "#f3f4f6", // Gris si pas de modif
                    color: isModified ? "#991b1b" : "#6b7280",
                  }}
                >
                  <TrendingDown
                    size={14}
                    style={{ transform: "rotate(180deg)" }}
                  />
                  <span>
                    {isModified
                      ? `Hausse : ${Math.abs(gainMensuel).toLocaleString()} XPF`
                      : "Pas de changement"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <p
              style={{
                fontSize: "0.9rem",
                color: "#6b7280",
                margin: "0 0 0.5rem 0",
              }}
            >
              Intérêts total
            </p>

            <div className="comparison-row">
              <span
                style={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "#9ca3af",
                  textDecoration: "line-through",
                }}
              >
                {interetsRestantsActuels.toLocaleString()} XPF
              </span>
              <ArrowRight
                size={20}
                strokeWidth={3}
                color="#6b7280"
                className="arrow-icon"
              />
              <span
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: isModified ? "#2563eb" : "#9ca3af",
                }}
              >
                {(isModified
                  ? nouveauxInteretsTotaux
                  : interetsRestantsActuels
                ).toLocaleString()}{" "}
                XPF
              </span>
            </div>

            <div
              style={{
                backgroundColor: "white",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "1px solid #e5e7eb",
                marginTop: "0.5rem",
              }}
            >
              {/* LIGNE 1 : GAIN BRUT OU SURCOÛT INTÉRÊTS */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.85rem",
                  color: "#6b7280",
                  marginBottom: "0.25rem",
                }}
              >
                <span>
                  {gainInterets >= 0 ? "Gain intérêts :" : "Surcoût intérêts :"}
                </span>
                <span
                  style={{
                    fontWeight: gainInterets < 0 ? "bold" : "normal",
                    color: gainInterets < 0 ? "#991b1b" : "#6b7280",
                  }}
                >
                  {gainInterets > 0 ? "+" : ""}
                  {gainInterets.toLocaleString()} XPF
                </span>
              </div>

              {fraisNego > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.85rem",
                    color: "#9a3412",
                    marginBottom: "0.25rem",
                  }}
                >
                  <span>Frais négo :</span>
                  <span>- {Math.round(fraisNego).toLocaleString()} XPF</span>
                </div>
              )}

              {/* LIGNE 3 : GAIN NET OU SURCOÛT TOTAL */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  borderTop: "1px solid #f3f4f6",
                  paddingTop: "0.25rem",
                  marginTop: "0.25rem",
                  color:
                    gainNet > 0
                      ? "#166534"
                      : gainNet < 0
                      ? "#991b1b"
                      : "#374151",
                }}
              >
                <span>{gainNet >= 0 ? "Gain net :" : "Surcoût total :"}</span>
                <span>
                  {gainNet > 0 ? "+" : ""}
                  {Math.round(gainNet).toLocaleString()} XPF
                </span>
              </div>
            </div>
          </div>

          {/* VERDICT : UNIQUEMENT SI MODIFIÉ */}
          {isModified && (
            <div
              style={
                verdict.type === "success"
                  ? styles.successBox
                  : verdict.type === "danger"
                  ? styles.alertBox
                  : styles.warningBox
              }
            >
              {verdict.type === "success" ? (
                <CheckCircle size={24} style={{ flexShrink: 0 }} />
              ) : (
                <AlertCircle size={24} style={{ flexShrink: 0 }} />
              )}
              <div>
                <strong>{verdict.title}</strong>
                <p style={{ margin: 0, marginTop: "0.25rem" }}>
                  {verdict.text}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// REGROUPEMENT VIEW
// ------------------------------------------------------------------
const RegroupementView: React.FC<{ credits: Credit[]; onBack: () => void }> = ({
  credits,
  onBack,
}) => {
  const [selectedCreditIds, setSelectedCreditIds] = useState<string[]>([]);

  const [tresorerie, setTresorerie] = useState<number | "">("");
  const [includeTresorerie, setIncludeTresorerie] = useState<boolean>(false);

  const [apport, setApport] = useState<number | "">("");
  const [includeApport, setIncludeApport] = useState<boolean>(false);

  const [nouveauTaux, setNouveauTaux] = useState<number>(4.5);
  const [nouvelleDureeMois, setNouvelleDureeMois] = useState<number>(240);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedCredits = credits.filter((c) =>
    selectedCreditIds.includes(c.id)
  );

  const defaultDuration =
    selectedCredits.length > 0
      ? Math.max(...selectedCredits.map((c) => calculerDureeRestanteMois(c)))
      : 240;

  const defaultRate =
    selectedCredits.length > 0
      ? Number(Math.min(...selectedCredits.map((c) => c.taux * 100)).toFixed(2))
      : 4.5;

  useEffect(() => {
    if (selectedCredits.length > 0) {
      setNouvelleDureeMois(Math.min(300, defaultDuration));
      setNouveauTaux(defaultRate);
    }
  }, [selectedCreditIds]);

  const toggleCredit = (id: string) => {
    if (selectedCreditIds.includes(id))
      setSelectedCreditIds(selectedCreditIds.filter((cId) => cId !== id));
    else setSelectedCreditIds([...selectedCreditIds, id]);
  };

  const formatCurrency = (val: number | "") =>
    val === "" ? "" : val.toLocaleString("fr-FR");
  const parseCurrency = (val: string) => {
    const parsed = parseInt(val.replace(/\s/g, ""), 10);
    return isNaN(parsed) ? "" : parsed;
  };

  const montantTresorerie = includeTresorerie ? Number(tresorerie) || 0 : 0;
  const montantApport = includeApport ? Number(apport) || 0 : 0;

  const totalCapitalRestant = selectedCredits.reduce(
    (sum, c) => sum + calculerCapitalRestantAujourdhui(c),
    0
  );
  const totalMensualitesActuelles = selectedCredits.reduce(
    (sum, c) => sum + calculerMensualite(c.capital, c.taux, c.dureeAnnees),
    0
  );
  const totalInteretsRestantsActuels =
    calculerInteretsRestantsAujourdhui(selectedCredits);

  const totalAFinancer = Math.max(
    0,
    totalCapitalRestant + montantTresorerie - montantApport
  );

  // --- NOUVEAUX CALCULS SÉPARÉS (Logique d'apport corrigée) ---
  const nouvelleMensualiteCalculée = calculerMensualite(
    totalAFinancer,
    nouveauTaux / 100,
    nouvelleDureeMois / 12
  );

  // L'apport vient d'abord réduire la trésorerie. S'il en reste, il réduit le rachat des crédits.
  const tresorerieNette = Math.max(0, montantTresorerie - montantApport);
  const resteApport = Math.max(0, montantApport - montantTresorerie);
  const capitalRachatNet = Math.max(0, totalCapitalRestant - resteApport);

  // 1. Calcul des intérêts UNIQUEMENT sur la partie Rachat
  const mensualiteRachatSeul =
    capitalRachatNet > 0
      ? calculerMensualite(
          capitalRachatNet,
          nouveauTaux / 100,
          nouvelleDureeMois / 12
        )
      : 0;
  const interetsRachatSeul = Math.max(
    0,
    Math.round(mensualiteRachatSeul * nouvelleDureeMois - capitalRachatNet)
  );

  // 2. Calcul des intérêts UNIQUEMENT sur le crédit supplémentaire (Trésorerie nette)
  const mensualiteTresorerie =
    tresorerieNette > 0
      ? calculerMensualite(
          tresorerieNette,
          nouveauTaux / 100,
          nouvelleDureeMois / 12
        )
      : 0;
  const interetsTresorerie = Math.max(
    0,
    Math.round(mensualiteTresorerie * nouvelleDureeMois - tresorerieNette)
  );
  // ---------------------------------

  const isModified =
    selectedCredits.length >= 2 ||
    Math.abs(nouveauTaux - defaultRate) > 0.01 ||
    nouvelleDureeMois !== Math.min(300, defaultDuration) ||
    montantTresorerie > 0 ||
    montantApport > 0;

  const nouvelleMensualite = isModified
    ? nouvelleMensualiteCalculée
    : totalMensualitesActuelles;
  const gainMensuel = isModified
    ? totalMensualitesActuelles - nouvelleMensualite
    : 0;
  const gainInterets = isModified
    ? totalInteretsRestantsActuels - interetsRachatSeul
    : 0;

  const phraseSynthese =
    selectedCredits.length === 0
      ? "Veuillez sélectionner au moins un crédit pour voir la simulation."
      : `Opération : Rachat de ${selectedCredits.length} crédit(s)` +
        (montantTresorerie > 0
          ? ` avec un projet supplémentaire de ${montantTresorerie.toLocaleString()} XPF`
          : "") +
        (montantApport > 0
          ? ` et un apport de ${montantApport.toLocaleString()} XPF.`
          : ".") +
        ` Le nouveau financement porte sur un montant total de ${totalAFinancer.toLocaleString()} XPF, au taux de ${nouveauTaux.toFixed(
          2
        )}% sur ${nouvelleDureeMois.toLocaleString()} mois.`;

  // --- NOUVEAU : Le cerveau pédagogique de l'application ---
  const annees = (nouvelleDureeMois / 12).toFixed(1);
  const moisEntiers = Math.round(nouvelleDureeMois);

  let titreExplication = "L'analyse de votre opération :";
  let explicationMensualite = "";
  let explicationInterets = "";
  let explicationProjet = "";

  if (tresorerieNette === 0) {
    // ==========================================
    // CAS 1 : SANS PROJET SUPPLEMENTAIRE (Regroupement uniquement)
    // ==========================================
    
    // Scénario A : Baisse mensualité & Baisse intérêts (L'opération parfaite)
    if (gainMensuel >= 0 && gainInterets >= 0) {
      explicationMensualite = `✅ Opération parfaite : Vous regagnez du pouvoir d'achat en réduisant vos paiements de ${gainMensuel.toLocaleString()} XPF par mois.`;
      explicationInterets = `✅ Et le meilleur : grâce au taux optimisé de ${nouveauTaux.toFixed(2)}% sur ${moisEntiers} mois, vous économisez également ${gainInterets.toLocaleString()} XPF sur le coût total de vos crédits ! 💡 Astuce : Vous pouvez raccourcir un peu la durée pour économiser encore plus d'intérêts, tant que la mensualité vous convient.`;
    }
    
    // Scénario B : Baisse mensualité & Hausse intérêts (Le prix du confort)
    else if (gainMensuel >= 0 && gainInterets < 0) {
      explicationMensualite = `✅ Objectif souffle : Vous abaissez vos paiements de ${gainMensuel.toLocaleString()} XPF par mois. C'est idéal pour soulager votre budget au quotidien.`;
      explicationInterets = `⚠️ Le prix du confort : En étalant la dette sur ${moisEntiers} mois pour baisser la mensualité, cela génère un surcoût de ${Math.abs(gainInterets).toLocaleString()} XPF d'intérêts. 💡 Astuce : Si vous voulez réduire ce coût global, essayez de raccourcir un peu la durée avec les boutons -/+.`;
    }
    
    // Scénario C : Hausse mensualité & Baisse intérêts (Le remboursement express)
    else if (gainMensuel < 0 && gainInterets >= 0) {
      explicationMensualite = `⚠️ Effort mensuel : Votre mensualité globale augmente de ${Math.abs(gainMensuel).toLocaleString()} XPF. Assurez-vous que votre budget vous le permet.`;
      explicationInterets = `✅ Stratégie gagnante : En acceptant de payer plus chaque mois (sur ${moisEntiers} mois), vous remboursez beaucoup plus vite et économisez ${gainInterets.toLocaleString()} XPF d'intérêts ! 💡 Astuce : Si cet effort mensuel est trop lourd, allongez légèrement la durée.`;
    }
    
    // Scénario D : Hausse mensualité & Hausse intérêts (L'alerte rouge)
    else if (gainMensuel < 0 && gainInterets < 0) {
      explicationMensualite = `⚠️ Attention : Votre mensualité augmente de ${Math.abs(gainMensuel).toLocaleString()} XPF. Assurez-vous que votre budget vous le permet.`;
      explicationInterets = `⚠️ Coût global : En plus de la hausse mensuelle, cette configuration sur ${moisEntiers} mois génère un surcoût de ${Math.abs(gainInterets).toLocaleString()} XPF d'intérêts. 💡 Conseil : Cette simulation n'est pas à votre avantage. Essayez d'allonger la durée pour faire baisser la mensualité, ou vérifiez si le nouveau taux proposé est vraiment plus intéressant que vos taux actuels.`;
    }
  } else {
    // ==========================================
    // CAS 2 : AVEC PROJET SUPPLEMENTAIRE
    // ==========================================
    titreExplication = "L'analyse de votre projet global :";

    // Scénario A : Baisse mensualité & Baisse intérêts (Le rêve absolu)
    if (gainMensuel > 0 && gainInterets >= 0) {
      explicationMensualite = `✅ Résultat exceptionnel : Malgré le crédit supplémentaire, votre mensualité globale baisse de ${gainMensuel.toLocaleString()} XPF ! Vous gagnez en pouvoir d'achat.`;
      explicationInterets = `✅ Et ce n'est pas tout : l'optimisation de votre taux (${nouveauTaux.toFixed(
        2
      )}%) et de la durée (${moisEntiers} mois) vous permet même d'économiser ${gainInterets.toLocaleString()} XPF sur les intérêts de vos crédits regroupés. 💡 Astuce : Si vous souhaitez économiser encore plus d'intérêts, réduisez légèrement la durée, tant que la mensualité reste confortable pour vous.`;
    }

    // Scénario B : Baisse mensualité & Hausse intérêts (Pour respirer financièrement)
    else if (gainMensuel > 0 && gainInterets < 0) {
      explicationMensualite = `✅ Vous intégrez un nouveau projet tout en réduisant votre mensualité globale de ${gainMensuel.toLocaleString()} XPF. Cela vous donne de l'air au quotidien.`;
      explicationInterets = `⚠️ La contrepartie : Pour faire baisser cette mensualité malgré le nouveau prêt, la dette est étalée sur ${moisEntiers} mois. Cela génère un surcoût de ${Math.abs(
        gainInterets
      ).toLocaleString()} XPF uniquement sur vos crédits regroupés. 💡 Astuce : Si vous voulez réduire ces intérêts, essayez de raccourcir un peu la durée avec les boutons -/+ (la mensualité remontera un peu).`;
    }

    // Scénario C : Hausse mensualité & Baisse intérêts (Le remboursement express)
    else if (gainMensuel < 0 && gainInterets >= 0) {
      explicationMensualite = `⚠️ Effort mensuel : L'intégration de votre nouveau projet fait logiquement augmenter votre mensualité de ${Math.abs(
        gainMensuel
      ).toLocaleString()} XPF chaque mois.`;
      explicationInterets = `✅ Stratégie payante : En acceptant cette mensualité plus élevée sur ${moisEntiers} mois, vous remboursez plus vite ! Résultat : vous financez votre projet ET vous réalisez un gain d’intérêt de ${gainInterets.toLocaleString()} XPF sur vos crédits regroupés. 💡 Astuce : Si la mensualité est trop lourde, allongez légèrement la durée.`;
    }

    // Scénario D : Hausse mensualité & Hausse intérêts (La charge lourde)
    else if (gainMensuel < 0 && gainInterets < 0) {
      explicationMensualite = `⚠️ Effort mensuel : L'intégration de votre nouveau projet fait logiquement augmenter votre mensualité de ${Math.abs(
        gainMensuel
      ).toLocaleString()} XPF chaque mois. Assurez-vous que cela respecte votre taux d'endettement.`;
      explicationInterets = `⚠️ Coût global : L'opération étalée sur ${moisEntiers} mois génère un surcoût d’intérêts de ${Math.abs(
        gainInterets
      ).toLocaleString()} XPF uniquement sur vos crédits regroupés. 💡 Le bon réglage selon vos objectifs : Pour payer moins d'intérêts, réduisez la durée ou négociez le taux. Pour réduire votre mensualité, allongez la durée. À vous de trouver le juste équilibre avec les boutons -/+ !`;
    }

    explicationProjet = `💡 Pour information, votre projet supplémentaire (net d'apport) de ${tresorerieNette.toLocaleString()} XPF génère à lui seul ${interetsTresorerie.toLocaleString()} XPF d'intérêts. L'avantage est de n'avoir qu'un seul prélèvement par mois pour l'ensemble.`;
  }

  // --------------------------------------------------------

  return (
    <div className="view-container" style={styles.viewContainer}>
      <button style={styles.backButton} onClick={onBack}>
        ← Retour au dashboard
      </button>
      <h1 style={styles.viewTitle}>Regrouper vos crédits</h1>
      <p style={styles.viewSubtitle}>
        Sélectionnez vos crédits à regrouper et configurez votre nouveau
        financement.
      </p>

      <div className="main-grid">
        {/* COLONNE GAUCHE */}
        <div>
          <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              marginBottom: "1rem",
              color: "#1f2937",
            }}
          >
            1. Sélectionner le(s) crédit(s)
          </h3>
          {credits.map((credit) => {
            const isSelected = selectedCreditIds.includes(credit.id);
            const capitalRestant = calculerCapitalRestantAujourdhui(credit);
            const mensualite = calculerMensualite(
              credit.capital,
              credit.taux,
              credit.dureeAnnees
            );
            const duree = calculerDureeRestanteMois(credit);
            const interets = calculerInteretsRestantsAujourdhui([credit]);

            return (
              <div
                key={credit.id}
                onClick={() => toggleCredit(credit.id)}
                style={{
                  ...styles.creditSelectCard,
                  ...(isSelected ? styles.creditSelectCardActive : {}),
                  cursor: "pointer",
                }}
              >
                {isSelected ? (
                  <CheckSquare
                    color="#2563eb"
                    size={24}
                    style={{ flexShrink: 0 }}
                  />
                ) : (
                  <Square color="#9ca3af" size={24} style={{ flexShrink: 0 }} />
                )}

                <div style={{ width: "100%" }}>
                  <div
                    className="credit-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <p
                      style={{ fontWeight: "600", margin: 0, fontSize: "1rem" }}
                    >
                      {credit.nom}
                    </p>
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        color: "#2563eb",
                      }}
                    >
                      {mensualite.toLocaleString()} XPF/mois
                    </span>
                  </div>

                  <div
                    className="credit-details"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "0.5rem",
                      marginTop: "1rem",
                    }}
                  >
                    <div
                      className="credit-stat"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        CRD
                      </span>
                      <strong style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                        {capitalRestant.toLocaleString()} XPF
                      </strong>
                    </div>
                    <div
                      className="credit-stat"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        {" "}
                        Durée{" "}
                      </span>
                      <strong style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                        {duree} mois
                      </strong>
                    </div>
                    <div
                      className="credit-stat"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        {" "}
                        Taux
                      </span>
                      <strong style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                        {(credit.taux * 100).toFixed(2)}%
                      </strong>
                    </div>
                    <div
                      className="credit-stat"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        Intérêts restants
                      </span>
                      <strong style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                        {interets.toLocaleString()} XPF
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              margin: "1.5rem 0 1rem 0",
              color: "#1f2937",
            }}
          >
            2. Options de financement
          </h3>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={includeTresorerie}
                  onChange={(e) => setIncludeTresorerie(e.target.checked)}
                />
                <span
                  style={{
                    fontSize: "0.9rem",
                    color: "#374151",
                    fontWeight: "500",
                  }}
                >
                  Crédit supplémentaire
                </span>
              </label>
              {includeTresorerie && (
                <input
                  type="text"
                  inputMode="numeric"
                  style={{
                    ...styles.input,
                    marginTop: "0.25rem",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                  value={formatCurrency(tresorerie)}
                  onChange={(e) => setTresorerie(parseCurrency(e.target.value))}
                />
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={includeApport}
                  onChange={(e) => setIncludeApport(e.target.checked)}
                />
                <span
                  style={{
                    fontSize: "0.9rem",
                    color: "#374151",
                    fontWeight: "500",
                  }}
                >
                  Apport perso
                </span>
              </label>
              {includeApport && (
                <input
                  type="text"
                  inputMode="numeric"
                  style={{
                    ...styles.input,
                    marginTop: "0.25rem",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                  value={formatCurrency(apport)}
                  onChange={(e) => setApport(parseCurrency(e.target.value))}
                />
              )}
            </div>
          </div>

          <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              margin: "1.5rem 0 1rem 0",
              color: "#1f2937",
            }}
          >
            3. Nouveau Financement
          </h3>

          <div style={styles.rangeContainer}>
            <div style={styles.rangeLabel}>
              <span>Nouveau Taux</span>
              <span style={{ color: "#2563eb" }}>
                {nouveauTaux.toFixed(2)}%
              </span>
            </div>
            <div className="input-row">
              <input
                type="range"
                min="0.2"
                max="10"
                step="0.05"
                value={nouveauTaux}
                onChange={(e) => setNouveauTaux(parseFloat(e.target.value))}
                style={styles.rangeInput}
              />
              <input
                type="number"
                inputMode="decimal"
                step="0.05"
                value={nouveauTaux}
                onChange={(e) => setNouveauTaux(parseFloat(e.target.value))}
                style={{ ...styles.input, width: "100px" }}
              />
            </div>
          </div>

          <div
            style={{
              ...styles.rangeContainer,
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div style={styles.rangeLabel}>
              <span>Nouvelle Durée</span>
              <span style={{ color: "#2563eb", fontWeight: "bold" }}>
                {(nouvelleDureeMois / 12).toFixed(1)} ans (
                {Math.round(nouvelleDureeMois)} mois)
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <button
                onClick={() =>
                  setNouvelleDureeMois(Math.max(1, nouvelleDureeMois - 1))
                }
                style={{
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  width: "40px",
                  height: "40px",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                -
              </button>

              <div
                style={{
                  position: "relative",
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0.1"
                  max="30"
                  value={Number((nouvelleDureeMois / 12).toFixed(2))}
                  onChange={(e) => {
                    const annees = parseFloat(e.target.value);
                    if (!isNaN(annees))
                      setNouvelleDureeMois(Math.round(annees * 12));
                  }}
                  style={{
                    ...styles.input,
                    width: "100%",
                    textAlign: "center",
                    fontSize: "1.1rem",
                    paddingRight: "3rem",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    right: "1rem",
                    color: "#6b7280",
                    fontSize: "0.9rem",
                  }}
                >
                  ans
                </span>
              </div>

              <button
                onClick={() =>
                  setNouvelleDureeMois(Math.min(360, nouvelleDureeMois + 1))
                }
                style={{
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  width: "40px",
                  height: "40px",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                +
              </button>
            </div>
            <p
              style={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                textAlign: "center",
                margin: 0,
              }}
            >
              Astuce : Utilisez les boutons -/+ pour ajuster au mois près.
            </p>
          </div>
        </div>

        {/* COLONNE DROITE : Résultats */}
        <div>
          <div
            style={{
              backgroundColor: "#f3f4f6",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              marginBottom: "2rem",
            }}
          >
            <h4
              style={{
                fontSize: "1rem",
                fontWeight: "bold",
                marginBottom: "1rem",
                color: "#374151",
              }}
            >
              Structure du financement
            </h4>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "0.5rem",
              }}
            >
              <span>Capital racheté</span>
              <span style={{ fontWeight: "600" }}>
                {totalCapitalRestant.toLocaleString()} XPF
              </span>
            </div>

            {montantTresorerie > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "0.5rem",
                }}
              >
                <span>+ Crédit suppl.</span>
                <span style={{ fontWeight: "600" }}>
                  {montantTresorerie.toLocaleString()} XPF
                </span>
              </div>
            )}

            {montantApport > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "0.5rem",
                }}
              >
                <span>- Apport</span>
                <span style={{ fontWeight: "600", color: "#16a34a" }}>
                  - {montantApport.toLocaleString()} XPF
                </span>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "2px solid #d1d5db",
                marginTop: "0.5rem",
                paddingTop: "0.5rem",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#1f2937" }}>
                TOTAL À EMPRUNTER
              </span>
              <span
                style={{
                  fontWeight: "bold",
                  color: "#2563eb",
                  fontSize: "1.1rem",
                }}
              >
                {totalAFinancer.toLocaleString()} XPF
              </span>
            </div>
          </div>

          <div style={styles.resultsCard}>
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
                color: "#1f2937",
              }}
            >
              Bilan
            </h3>
            <p
              style={{
                fontSize: "0.85rem",
                color: "#6b7280",
                marginBottom: "1.5rem",
                fontStyle: "italic",
                lineHeight: "1.4",
              }}
            >
              {phraseSynthese}
            </p>

            <div
              style={{
                marginBottom: "1.5rem",
                paddingBottom: "1rem",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <p
                style={{
                  fontSize: "0.9rem",
                  color: "#6b7280",
                  margin: "0 0 0.5rem 0",
                }}
              >
                Mensualité totale
              </p>

              <div className="comparison-row">
                <span
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#9ca3af",
                    textDecoration: "line-through",
                  }}
                >
                  {totalMensualitesActuelles.toLocaleString()} XPF
                </span>
                <ArrowRight
                  size={20}
                  strokeWidth={3}
                  color="#6b7280"
                  className="arrow-icon"
                />
                <span
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color:
                      isModified && selectedCredits.length > 0
                        ? "#2563eb"
                        : "#9ca3af",
                  }}
                >
                  {nouvelleMensualite.toLocaleString()} XPF
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  marginTop: "0.5rem",
                }}
              >
                {gainMensuel > 0 ? (
                  <div
                    style={{
                      ...styles.badge,
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                    }}
                  >
                    <TrendingDown size={14} /> Gain mensuel :{" "}
                    {gainMensuel.toLocaleString()} XPF
                  </div>
                ) : (
                  <div
                    style={{
                      ...styles.badge,
                      backgroundColor:
                        isModified && selectedCredits.length > 0
                          ? "#fee2e2"
                          : "#f3f4f6",
                      color:
                        isModified && selectedCredits.length > 0
                          ? "#991b1b"
                          : "#6b7280",
                    }}
                  >
                    <TrendingDown
                      size={14}
                      style={{ transform: "rotate(180deg)" }}
                    />
                    {isModified && selectedCredits.length > 0
                      ? `Hausse : ${Math.abs(gainMensuel).toLocaleString()} XPF`
                      : "Pas de changement"}
                  </div>
                )}
              </div>
            </div>

            {/* --- SECTION INTÉRÊTS (Dynamique) --- */}
            {tresorerieNette > 0 ? (
              // VUE 1 : S'il y a un projet supplémentaire
              <div
                style={{
                  marginBottom: "1.5rem",
                  backgroundColor: "#f8fafc",
                  padding: "1.5rem",
                  borderRadius: "0.75rem",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Total Centré */}
                <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "#6b7280",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    Intérêts nouveau financement
                  </p>
                  <span
                    style={{
                      fontSize: "2rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {(interetsRachatSeul + interetsTresorerie).toLocaleString()}{" "}
                    XPF
                  </span>
                </div>

                {/* Détail de la répartition */}

                {/* Ligne Projet Supp */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#4b5563",
                      fontWeight: "400",
                    }}
                  >
                    Intérêts liés au crédit supplémentaire
                  </span>
                  <span style={{ fontWeight: "500", color: "#1f2937" }}>
                    {interetsTresorerie.toLocaleString()} XPF
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    borderTop: "1px dashed #cbd5e1",
                    paddingTop: "1.25rem",
                  }}
                >
                  {/* Ligne Regroupement */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center", // Remis centré verticalement car plus de badge
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.85rem",
                        color: "#4b5563",
                        fontWeight: "400",
                      }}
                    >
                      Intérêts liés au regroupement de crédits
                    </span>
                    <span
                      style={{
                        fontWeight: "500",
                        color: "#1f2937",
                      }}
                    >
                      {interetsRachatSeul.toLocaleString()} XPF
                    </span>
                    
                  </div>
                </div>
              </div>
            ) : (
              // VUE 2 : S'il n'y a PAS de projet supplémentaire (Vue classique)
              <div style={{ marginBottom: "1.5rem" }}>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    margin: "0 0 0.5rem 0",
                  }}
                >
                  Intérêts
                </p>

                <div className="comparison-row">
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#9ca3af",
                      textDecoration: "line-through",
                    }}
                  >
                    {totalInteretsRestantsActuels.toLocaleString()} XPF
                  </span>
                  <ArrowRight
                    size={20}
                    strokeWidth={3}
                    color="#6b7280"
                    className="arrow-icon"
                  />
                  <span
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color:
                        isModified && selectedCredits.length > 0
                          ? "#1f2937"
                          : "#9ca3af",
                    }}
                  >
                    {interetsRachatSeul.toLocaleString()} XPF
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    marginTop: "0.5rem",
                  }}
                >
                  {gainInterets > 0 ? (
                    <div
                      style={{
                        ...styles.badge,
                        backgroundColor: "#dcfce7",
                        color: "#166534",
                      }}
                    >
                      <CheckCircle size={14} /> Gain intérêts :{" "}
                      {gainInterets.toLocaleString()} XPF
                    </div>
                  ) : (
                    <div
                      style={{
                        ...styles.badge,
                        backgroundColor:
                          isModified && selectedCredits.length > 0
                            ? "#fee2e2"
                            : "#f3f4f6",
                        color:
                          isModified && selectedCredits.length > 0
                            ? "#991b1b"
                            : "#6b7280",
                      }}
                    >
                      <AlertCircle size={14} />
                      {isModified && selectedCredits.length > 0
                        ? `Surcoût intérêts : ${Math.abs(
                            gainInterets
                          ).toLocaleString()} XPF`
                        : "Pas de changement"}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* ------------------------------------------ */}

            {/* --- NOUVEAU : MESSAGE EXPLICATIF GLOBAL ET PÉDAGOGIQUE --- */}
            {isModified && selectedCredits.length > 0 && (
              <div
                style={{
                  marginTop: "1.5rem",
                  padding: "1.5rem",
                  backgroundColor: "#fdf8f6", // Fond orange très clair pour attirer l'oeil
                  borderRadius: "0.75rem",
                  border: "1px solid #fed7aa", // Bordure orange clair
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem", // Espacement naturel entre les paragraphes
                }}
              >
                <h4
                  style={{
                    fontSize: "1rem",
                    fontWeight: "bold",
                    color: "#9a3412",
                    margin: "0",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Info size={18} />
                  L'analyse de votre opération :
                </h4>

                {/* Paragraphe 1 : L'impact sur le portefeuille mensuel */}
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "#431407",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  <strong>Mensualité : </strong>
                  {explicationMensualite}
                </p>

                {/* Paragraphe 2 : L'impact sur le coût global */}
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "#431407",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  <strong>Coût du rachat : </strong>
                  {explicationInterets}
                </p>

                {/* Paragraphe 3 : L'explication du projet (Seulement s'il y en a un) */}
                {tresorerieNette > 0 && (
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#431407",
                      margin: 0,
                      lineHeight: "1.5",
                      paddingTop: "0.5rem",
                      borderTop: "1px dashed #fdba74",
                    }}
                  >
                    {explicationProjet}
                  </p>
                )}
              </div>
            )}
            {/* ------------------------------------------- */}

            <div
              style={{
                marginTop: "1.5rem",
                paddingTop: "1rem",
                borderTop: "1px dashed #d1d5db",
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
              }}
            >
              <AlertCircle
                size={16}
                color="#9ca3af"
                style={{ flexShrink: 0, marginTop: "0.1rem" }}
              />
              <p
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                <strong style={{ color: "#4b5563" }}>À noter :</strong> Ce bilan
                n'inclut pas les frais éventuels liés au rachat (frais de
                dossier, indemnités de remboursement anticipé, frais de main
                levée d'hypothèque).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// REMBOURSEMENT ANTICIPÉ (NOUVEAU - MULTI CRÉDITS)
// ------------------------------------------------------------------
const RemboursementView: React.FC<{
  credits: Credit[];
  onBack: () => void;
}> = ({ credits, onBack }) => {
  // 1. DÉMARRE AVEC 0 CRÉDIT SÉLECTIONNÉ
  const [selectedCreditIds, setSelectedCreditIds] = useState<string[]>([]);
  
  // Le map stocke désormais des <number | ""> pour permettre de vider le champ
  const [repaymentMap, setRepaymentMap] = useState<Record<string, number | "">>({});
  const [tauxIRA, setTauxIRA] = useState<number>(3); // Default 3% generic

  // Force le scroll en haut de page à l'ouverture du composant
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedCredits = credits.filter((c) =>
    selectedCreditIds.includes(c.id)
  );

  // Initialise le montant à rembourser par défaut (Totalité du CRD)
  useEffect(() => {
    const newMap = { ...repaymentMap };
    selectedCredits.forEach((c) => {
      if (newMap[c.id] === undefined) {
        newMap[c.id] = calculerCapitalRestantAujourdhui(c);
      }
    });
    setRepaymentMap(newMap);
  }, [selectedCreditIds]);

  const toggleCredit = (id: string) => {
    if (selectedCreditIds.includes(id))
      setSelectedCreditIds(selectedCreditIds.filter((cId) => cId !== id));
    else setSelectedCreditIds([...selectedCreditIds, id]);
  };

  const handleRepaymentChange = (id: string, value: number | "") => {
    setRepaymentMap((prev) => ({ ...prev, [id]: value }));
  };

  // --- FONCTIONS DE FORMATAGE (Saisie fluide avec espaces) ---
  const formatCurrency = (val: number | "" | undefined) => 
    (val === "" || val === undefined) ? "" : val.toLocaleString("fr-FR");
    
  const parseCurrency = (val: string) => {
    const parsed = parseInt(val.replace(/\s/g, ""), 10);
    return isNaN(parsed) ? "" : parsed;
  };

  // --- CALCULS GLOBAUX AVANT ---
  const totalMensualitesAvant = credits.reduce(
    (sum, c) => sum + calculerMensualite(c.capital, c.taux, c.dureeAnnees),
    0
  );
  const totalCRDAvant = credits.reduce(
    (sum, c) => sum + calculerCapitalRestantAujourdhui(c),
    0
  );
  const totalInteretsAvant = calculerInteretsRestantsAujourdhui(credits);

  // --- CALCULS GLOBAUX APRÈS ---
  let totalMensualitesApres = totalMensualitesAvant;
  let totalCRDApres = totalCRDAvant;
  let totalInteretsApres = totalInteretsAvant;
  let totalFraisIRA = 0;
  let totalRembourse = 0;

  // Calculs détaillés par crédit sélectionné
  const impactDetails = selectedCredits.map((credit) => {
    const crdActuel = calculerCapitalRestantAujourdhui(credit);
    // On convertit en Number pour les calculs (si "" -> 0)
    const remboursementSaisi = Number(repaymentMap[credit.id]) || 0;
    const remboursementEffectif = Math.min(remboursementSaisi, crdActuel);

    const frais = Math.round(remboursementEffectif * (tauxIRA / 100));

    const nouveauCapital = crdActuel - remboursementEffectif;
    const mensualiteAvant = calculerMensualite(
      credit.capital,
      credit.taux,
      credit.dureeAnnees
    );
    const dureeRestante = calculerDureeRestanteMois(credit);

    let nouvelleMensualite = 0;
    let nouveauxInterets = 0;
    const interetsAvant = calculerInteretsRestantsAujourdhui([credit]);

    if (nouveauCapital <= 0) {
      nouvelleMensualite = 0;
      nouveauxInterets = 0;
    } else {
      const t = credit.taux / 12;
      const n = dureeRestante;
      if (t === 0) nouvelleMensualite = nouveauCapital / n;
      else
        nouvelleMensualite = Math.round(
          (nouveauCapital * t) / (1 - Math.pow(1 + t, -n))
        );
      nouveauxInterets = nouvelleMensualite * n - nouveauCapital;
    }

    // Mise à jour des totaux globaux
    totalMensualitesApres =
      totalMensualitesApres - mensualiteAvant + nouvelleMensualite;
    totalCRDApres = totalCRDApres - remboursementEffectif;
    totalInteretsApres = totalInteretsApres - interetsAvant + nouveauxInterets;
    totalFraisIRA += frais;
    totalRembourse += remboursementEffectif;

    return {
      credit,
      mensualiteAvant,
      nouvelleMensualite,
      gainMensuel: mensualiteAvant - nouvelleMensualite,
      interetsAvant,
      nouveauxInterets,
      gainInterets: interetsAvant - nouveauxInterets,
    };
  });

  const gainMensuelGlobal = totalMensualitesAvant - totalMensualitesApres;
  const gainInteretsGlobal = totalInteretsAvant - totalInteretsApres;
  const gainCRDGlobal = totalCRDAvant - totalCRDApres;
  const totalAPayer = totalRembourse + totalFraisIRA;

  return (
    <div className="view-container" style={styles.viewContainer}>
      <button style={styles.backButton} onClick={onBack}>
        ← Retour au dashboard
      </button>
      <h1 style={styles.viewTitle}>Remboursement anticipé</h1>
      <p style={styles.viewSubtitle}>
        Simulez l'impact d'un remboursement partiel ou total sur vos crédits.
      </p>

      {/* Utilisation de la classe CSS pour le responsive */}
      <div className="main-grid">
        
        {/* Colonne Gauche : Sélection & Saisie */}
        <div>
          <h3 style={{ fontSize: "1.25rem", fontWeight: "bold", marginBottom: "1rem", color: "#1f2937" }}>
            1. Sélectionner le(s) crédit(s)
          </h3>
          {credits.map((credit) => {
            const isSelected = selectedCreditIds.includes(credit.id);
            const crd = calculerCapitalRestantAujourdhui(credit);
            return (
              <div
                key={credit.id}
                onClick={() => toggleCredit(credit.id)}
                style={{
                  ...styles.creditSelectCard,
                  ...(isSelected ? styles.creditSelectCardActive : {}),
                  cursor: "pointer"
                }}
              >
                {isSelected ? (
                  <CheckSquare color="#2563eb" size={24} style={{ flexShrink: 0 }} />
                ) : (
                  <Square color="#9ca3af" size={24} style={{ flexShrink: 0 }} />
                )}
                <div style={{ width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: "600" }}>{credit.nom}</span>
                    <span style={{ fontSize: "0.9rem", color: "#6b7280" }}>
                      CRD: {crd.toLocaleString()} XPF
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: "2rem" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "bold", marginBottom: "1rem", color: "#1f2937" }}>
              2. Saisir les montants
            </h3>
            {selectedCredits.length === 0 && (
              <p style={{ color: "#6b7280" }}>Veuillez sélectionner au moins un crédit.</p>
            )}

            {selectedCredits.map((credit) => {
              const crd = calculerCapitalRestantAujourdhui(credit);
              return (
                <div key={credit.id} style={{ marginBottom: "1rem", backgroundColor: "white", padding: "1rem", borderRadius: "0.5rem", border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem", alignItems: "center" }}>
                    <span style={{ fontWeight: "600", color: "#374151" }}>
                      {credit.nom}
                    </span>
                    <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                      Max: {crd.toLocaleString()} XPF
                    </span>
                  </div>
                  <div style={styles.formGroup}>
                    {/* Saisie fluide avec séparateurs */}
                    <input
                      type="text"
                      inputMode="numeric"
                      style={{ ...styles.input, width: "100%", boxSizing: "border-box" }}
                      value={formatCurrency(repaymentMap[credit.id])}
                      onChange={(e) => handleRepaymentChange(credit.id, parseCurrency(e.target.value))}
                      placeholder="Montant à rembourser"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ ...styles.formGroup, marginTop: "2rem" }}>
            <label style={styles.label}>Taux des pénalités (IRA) %</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                style={{ ...styles.input, width: "100px" }}
                value={tauxIRA}
                onChange={(e) => setTauxIRA(parseFloat(e.target.value) || 0)}
              />
              <span style={{ fontSize: "0.9rem", color: "#ea580c", fontWeight: "500" }}>
                Total IRA : {totalFraisIRA.toLocaleString()} XPF
              </span>
            </div>
            <p style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic", marginTop: "0.5rem", lineHeight: "1.4" }}>
              Le taux d'IRA dépend de votre contrat. Généralement plafonné à 3%
              du CRD pour un prêt immo et 1% du montant remboursé pour un prêt conso.
            </p>
          </div>

          <div style={{ marginTop: "1.5rem", padding: "1rem", border: "1px solid #dbeafe", backgroundColor: "#eff6ff", borderRadius: "0.5rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <span style={{ color: "#1e40af" }}>Remboursement</span>
              <span style={{ fontWeight: "bold", color: "#1e3a8a" }}>
                {totalRembourse.toLocaleString()} XPF
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <span style={{ color: "#9a3412" }}>+ Frais IRA</span>
              <span style={{ fontWeight: "bold", color: "#c2410c" }}>
                {totalFraisIRA.toLocaleString()} XPF
              </span>
            </div>
            <div style={{ borderTop: "1px solid #bfdbfe", paddingTop: "0.5rem", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: "bold", color: "#1e3a8a" }}>
                TOTAL À PAYER
              </span>
              <span style={{ fontWeight: "bold", fontSize: "1.1rem", color: "#2563eb" }}>
                {totalAPayer.toLocaleString()} XPF
              </span>
            </div>
          </div>
        </div>

        {/* Colonne Droite : Impact sur l'opération */}
        <div style={{ ...styles.resultsCard, marginTop: 0 }}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: "bold", marginBottom: "1.5rem", color: "#1f2937" }}>
            Impact sur l'opération
          </h3>

          {impactDetails.length === 0 ? (
            <p style={{ color: "#6b7280", fontStyle: "italic" }}>
              Sélectionnez des crédits pour voir l'impact.
            </p>
          ) : (
            impactDetails.map((detail, index) => (
              <div
                key={detail.credit.id}
                style={{
                  marginBottom: "1.5rem",
                  paddingBottom: "1.5rem",
                  borderBottom: index < impactDetails.length - 1 ? "1px solid #e5e7eb" : "none",
                }}
              >
                <h4 style={{ fontSize: "1rem", fontWeight: "bold", color: "#374151", marginBottom: "1rem" }}>
                  Crédit {detail.credit.nom}
                </h4>

                {/* Mensualité - Format vertical mobile */}
                <div style={{ marginBottom: "1rem" }}>
                  <span style={{ justifyContent: "center", fontSize: "0.9rem", fontWeight: "bold", color: "#6b7280" }}>Mensualité</span>
                  <div className="comparison-row" style={{ marginTop: "0.4rem", backgroundColor: "#f9fafb", padding: "0.4rem", borderRadius: "0.5rem" }}>
                    <span style={{ fontSize: "0.9rem", color: "#6b7280", textDecoration: "line-through" }}>
                      {detail.mensualiteAvant.toLocaleString()} XPF
                    </span>
                    <ArrowRight size={16} color="#9ca3af" className="arrow-icon" />
                    <span style={{ fontWeight: "bold", color: "#1f2937" }}>
                      {detail.nouvelleMensualite.toLocaleString()} XPF
                    </span>
                  </div>
                  <div style={{ textAlign: "center", fontSize: "0.85rem", color: "#16a34a", fontWeight: "600", marginTop: "0.25rem" }}>
                    Gain: {detail.gainMensuel.toLocaleString()} XPF/mois
                  </div>
                </div>

                {/* Intérêts - Format vertical mobile */}
                <div>
                  <span style={{ justifyContent: "center", fontSize: "0.9rem", fontWeight: "bold", color: "#6b7280" }}>Intérêts</span>
                  <div className="comparison-row" style={{ marginTop: "0.4rem", backgroundColor: "#f9fafb", padding: "0.4rem", borderRadius: "0.5rem" }}>
                    <span style={{ fontSize: "0.9rem", color: "#6b7280", textDecoration: "line-through" }}>
                      {detail.interetsAvant.toLocaleString()} XPF
                    </span>
                    <ArrowRight size={16} color="#9ca3af" className="arrow-icon" />
                    <span style={{ fontWeight: "bold", color: "#1f2937" }}>
                      {Math.round(detail.nouveauxInterets).toLocaleString()} XPF
                    </span>
                  </div>
                  <div style={{ textAlign: "center", fontSize: "0.85rem", color: "#16a34a", fontWeight: "600", marginTop: "0.25rem" }}>
                    Économie: {detail.gainInterets.toLocaleString()} XPF
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Comparatif Global (En bas, pleine largeur) */}
      <div style={{ marginTop: "3rem" }}>
        <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "#1f2937" }}>
          Impact global (tous crédits)
        </h3>
        
        {/* On ajoute la classe "grid-stats" pour que les cartes s'empilent sur mobile */}
        <div className="grid-stats" style={styles.kpiGrid}>
          
          {/* KPI 1 : Mensualités */}
          <div style={{ ...styles.kpiCard, ...styles.kpiCardBlue }}>
            <div style={styles.kpiHeader}>
              <Wallet style={{ color: "#2563eb" }} size={24} />
              <h3 style={styles.kpiTitle}>Total Mensualités</h3>
            </div>
            <p style={{ ...styles.kpiValue, fontSize: "1.5rem", color: "#2563eb", textAlign: "center" }}>
              -{Math.round(gainMensuelGlobal).toLocaleString()} XPF
            </p>
            <div className="comparison-row" style={{ fontSize: "1rem", color: "#6b7280", marginTop: "0.75rem", justifyContent: "center" }}>
              <span>{Math.round(totalMensualitesAvant).toLocaleString()} XPF</span>
              <ArrowRight size={16} className="arrow-icon" />
              <span style={{ fontWeight: "bold" }}>{Math.round(totalMensualitesApres).toLocaleString()} XPF</span>
            </div>
          </div>

          {/* KPI 2 : Capital Restant */}
          <div style={{ ...styles.kpiCard, ...styles.kpiCardGreen }}>
            <div style={styles.kpiHeader}>
              <DollarSign style={{ color: "#16a34a" }} size={24} />
              <h3 style={styles.kpiTitle}>Total Dette (CRD)</h3>
            </div>
            <p style={{ ...styles.kpiValue, fontSize: "1.5rem", color: "#16a34a", textAlign: "center" }}>
              -{Math.round(gainCRDGlobal).toLocaleString()} XPF
            </p>
            <div className="comparison-row" style={{ fontSize: "1rem", color: "#6b7280", marginTop: "0.75rem", justifyContent: "center" }}>
              <span>{totalCRDAvant.toLocaleString()} XPF</span>
              <ArrowRight size={16} className="arrow-icon" />
              <span style={{ fontWeight: "bold" }}>{totalCRDApres.toLocaleString()} XPF</span>
            </div>
          </div>

          {/* KPI 3 : Intérêts */}
          <div style={{ ...styles.kpiCard, ...styles.kpiCardOrange }}>
            <div style={styles.kpiHeader}>
              <TrendingDown style={{ color: "#ea580c" }} size={24} />
              <h3 style={styles.kpiTitle}>Total Intérêts</h3>
            </div>
            <p style={{ ...styles.kpiValue, fontSize: "1.5rem", color: "#ea580c", textAlign: "center" }}>
              -{Math.round(gainInteretsGlobal).toLocaleString()} XPF
            </p>
            <div className="comparison-row" style={{ fontSize: "1rem", color: "#6b7280", marginTop: "0.75rem", justifyContent: "center" }}>
              <span>{totalInteretsAvant.toLocaleString()} XPF</span>
              <ArrowRight size={14} className="arrow-icon" />
              <span style={{ fontWeight: "bold" }}>{Math.round(totalInteretsApres).toLocaleString()} XPF</span>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

const AmortizationTable: React.FC<{ credits: Credit[] }> = ({ credits }) => {
  // 1. État : Par défaut, tous les crédits sont sélectionnés
  const [selectedCreditIds, setSelectedCreditIds] = useState<string[]>(
    credits.map((c) => c.id)
  );
  const [currentPage, setCurrentPage] = useState(0);

  // 2. PERFORMANCE (useMemo) : On ne recalcule le tableau que si la sélection change !
  const rows = useMemo(() => {
    const selectedCredits = credits.filter((c) =>
      selectedCreditIds.includes(c.id)
    );
    return consoliderTableaux(selectedCredits);
  }, [credits, selectedCreditIds]);

  const rowsPerPage = 50;
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const displayRows = rows.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  // 3. Logique de sélection (Intelligente)
  const toggleCredit = (id: string) => {
    setCurrentPage(0); // Retour à la page 1 si on change de filtre

    // CAS 1 : Si TOUS les crédits sont actuellement sélectionnés
    // Et qu'on clique sur un crédit précis, on isole ce crédit.
    if (selectedCreditIds.length === credits.length) {
      setSelectedCreditIds([id]);
      return;
    }

    if (selectedCreditIds.includes(id)) {
      // CAS 2 : On désélectionne un crédit
      const newSelection = selectedCreditIds.filter((cId) => cId !== id);

      // Sécurité : Si on décoche le tout dernier crédit, on réaffiche "Tous les crédits"
      if (newSelection.length === 0) {
        setSelectedCreditIds(credits.map((c) => c.id));
      } else {
        setSelectedCreditIds(newSelection);
      }
    } else {
      // CAS 3 : On ajoute un nouveau crédit à la sélection actuelle
      setSelectedCreditIds([...selectedCreditIds, id]);
    }
  };

  const selectAll = () => {
    setCurrentPage(0);
    setSelectedCreditIds(credits.map((c) => c.id));
  };

  return (
    <div style={styles.table}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            fontSize: "1.25rem",
            fontWeight: "bold",
            color: "#1f2937",
            margin: 0,
          }}
        >
          Tableau d'amortissement
        </h3>
      </div>

      {/* --- NOUVEAU : ZONE DE FILTRES --- */}
      {credits.length > 1 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "1.5rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <span
            style={{
              fontSize: "0.85rem",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              marginRight: "0.5rem",
            }}
          >
            Filtrer :
          </span>

          <button
            onClick={selectAll}
            style={{
              padding: "0.35rem 0.75rem",
              borderRadius: "9999px",
              fontSize: "0.8rem",
              fontWeight: "600",
              cursor: "pointer",
              border: "1px solid",
              transition: "all 0.2s",
              backgroundColor:
                selectedCreditIds.length === credits.length
                  ? "#2563eb"
                  : "#f3f4f6",
              color:
                selectedCreditIds.length === credits.length
                  ? "white"
                  : "#4b5563",
              borderColor:
                selectedCreditIds.length === credits.length
                  ? "#2563eb"
                  : "#d1d5db",
            }}
          >
            Tous les crédits
          </button>

          {credits.map((c) => {
            const isSelected = selectedCreditIds.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggleCredit(c.id)}
                style={{
                  padding: "0.35rem 0.75rem",
                  borderRadius: "9999px",
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  border: "1px solid",
                  transition: "all 0.2s",
                  backgroundColor:
                    isSelected && selectedCreditIds.length !== credits.length
                      ? "#eff6ff"
                      : "white",
                  color:
                    isSelected && selectedCreditIds.length !== credits.length
                      ? "#2563eb"
                      : "#4b5563",
                  borderColor:
                    isSelected && selectedCreditIds.length !== credits.length
                      ? "#bfdbfe"
                      : "#d1d5db",
                }}
              >
                {c.nom}
              </button>
            );
          })}
        </div>
      )}
      {/* --------------------------------- */}

      {/* Affichage du tableau ou message si rien n'est sélectionné */}
      {rows.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "#6b7280",
            fontStyle: "italic",
          }}
        >
          Sélectionnez au moins un crédit pour afficher le tableau.
        </div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.tableElement}>
              <thead>
                <tr>
                  <th style={styles.th}>Nb</th>
                  <th style={styles.th}>Date</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>
                    Capital restant dû
                  </th>
                  <th style={{ ...styles.th, ...styles.thRight }}>
                    Mensualité
                  </th>
                  <th
                    style={{
                      ...styles.th,
                      ...styles.thRight,
                      fontStyle: "italic",
                      fontWeight: "normal",
                    }}
                  >
                    dont amortissement
                  </th>
                  <th
                    style={{
                      ...styles.th,
                      ...styles.thRight,
                      fontStyle: "italic",
                      fontWeight: "normal",
                    }}
                  >
                    dont intérêts
                  </th>
                  <th style={{ ...styles.th, ...styles.thRight }}>
                    Intérêts cumulés
                  </th>
                  <th style={{ ...styles.th, ...styles.thRight }}>
                    Intérêts restants
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => (
                  <tr
                    key={row.mois}
                    style={{ transition: "background-color 0.2s" }}
                  >
                    <td style={styles.td}>{row.mois}</td>
                    <td style={styles.td}>{row.date}</td>
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        fontWeight: "600",
                        color: "#1f2937",
                      }}
                    >
                      {row.capitalRestant.toLocaleString()}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        fontWeight: "600",
                        color: "#2563eb",
                      }}
                    >
                      {row.mensualite.toLocaleString()}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        fontStyle: "italic",
                      }}
                    >
                      {row.capitalRembourse.toLocaleString()}
                    </td>
                
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        color: "#dc2626",
                        fontStyle: "italic",
                      }}
                    >
                      {row.interetsMensuels.toLocaleString()}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        fontWeight: "600",
                        color: "#f59e0b",
                      }}
                    >
                      {row.interetsCumules.toLocaleString()}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        fontWeight: "600",
                        color: "#ea580c",
                      }}
                    >
                      {row.interetsRestants.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                style={{
                  ...styles.paginationButton,
                  opacity: currentPage === 0 ? 0.5 : 1,
                  cursor: currentPage === 0 ? "not-allowed" : "pointer",
                }}
              >
                Précédent
              </button>
              <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                Page {currentPage + 1} sur {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
                }
                disabled={currentPage === totalPages - 1}
                style={{
                  ...styles.paginationButton,
                  opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                  cursor:
                    currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const [credits, setCredits] = useState<Credit[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState<Credit | null>(null);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("dashboard");
  const sessionId = getOrCreateSessionId();
  const [user, setUser] = useState<any>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [hasShownAuthPrompt, setHasShownAuthPrompt] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [newPassword, setNewPassword] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);

  // NOUVEAU : Détection de la taille de l'écran pour l'affichage responsive de ajouter un crédit
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 1. Initialiser Fathom au chargement de l'application
  useEffect(() => {
    // Remplace par ton vrai Site ID Fathom
    Fathom.load('EGWSTXOH', {
      includedDomains: ['iaoracredit.com', 'www.iaoracredit.com']
    });
  }, []);

  const loadCredits = async (userId: string) => {
    const { data, error } = await supabase
      .from("credits")
      .select("*")
      .eq("user_id", userId);

    if (error) {
      console.error("Erreur lors du chargement des crédits:", error);
    } else if (data) {
      const formattedData = data.map((c) => ({
        ...c,
        dateDebut: new Date(c.date_debut),
        dureeAnnees: c.duree_annees,
      }));
      setCredits(formattedData);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser && isPremium) loadCredits(currentUser.id);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (event === "PASSWORD_RECOVERY") {
        setViewMode("profile");
        alert(
          "Vous pouvez maintenant saisir votre nouveau mot de passe dans votre espace compte."
        );
      }

      if (currentUser && isPremium) {
        loadCredits(currentUser.id);
      } else if (!currentUser) {
        setCredits([]);
      }
    });

    return () => subscription.unsubscribe();
  }, [isPremium]);

  useEffect(() => {
    const savedCredits = localStorage.getItem("credits");
    if (savedCredits) {
      const parsed = JSON.parse(savedCredits);
      setCredits(
        parsed.map((c: any) => ({ ...c, dateDebut: new Date(c.dateDebut) }))
      );
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("credits", JSON.stringify(credits));
  }, [credits]);

  // NOUVEAU : Vérifier si l'utilisateur est Premium dans la base de données
  useEffect(() => {
    const checkPremiumStatus = async () => {
      // Si personne n'est connecté, on est en gratuit
      if (!user) {
        setIsPremium(false);
        return;
      }

      // On va interroger la table "profiles" pour cet utilisateur précis
      const { data, error } = await supabase
        .from("profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();

      // Si on trouve la ligne et que is_premium est vrai, on débloque l'app !
      if (data && data.is_premium) {
        setIsPremium(true);
      } else {
        setIsPremium(false);
      }
    };

    checkPremiumStatus();
  }, [user]); // Ce code s'exécute à chaque fois que "user" change

  // NOUVEAU : Nettoyer l'URL après un retour de paiement Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("payment_success") === "true") {
      // Nettoie l'URL pour ne garder que le nom de domaine propre
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Optionnel : tu pourrais même ajouter un petit message de succès ici !
      alert("Merci pour votre abonnement Premium 🎉");
    }
  }, []);

  const handleAddCredit = async (
    creditData: Omit<Credit, "id" | "userSessionId">
  ) => {
    // --- LIMITE FREEMIUM ---
    if (credits.length >= 1 && !isPremium) {
      setIsLimitModalOpen(true);
      return;
    }
    const newId = crypto.randomUUID();
    const newCredit: Credit = {
      ...creditData,
      id: newId,
      userSessionId: user ? user.id : sessionId,
    };

    setCredits([...credits, newCredit]);

    if (user && isPremium) {
      const { error } = await supabase.from("credits").insert([
        {
          id: newId,
          nom: creditData.nom,
          type: creditData.type,
          capital: creditData.capital,
          taux: creditData.taux,
          duree_annees: creditData.dureeAnnees,
          date_debut: creditData.dateDebut.toISOString().split("T")[0],
          user_id: user.id,
        },
      ]);
      if (error) {
        console.error("Erreur sauvegarde cloud:", error);
      }
    } else {
      console.log(
        "Sauvegarde locale uniquement (Utilisateur non-premium ou non-connecté)"
      );
    }
  };

  const handleEditCredit = async (
    creditData: Omit<Credit, "id" | "userSessionId">
  ) => {
    if (editingCredit) {
      setCredits(
        credits.map((c) =>
          c.id === editingCredit.id
            ? { ...creditData, id: c.id, userSessionId: c.userSessionId }
            : c
        )
      );

      if (user && isPremium) {
        const { error } = await supabase
          .from("credits")
          .update({
            nom: creditData.nom,
            type: creditData.type,
            capital: creditData.capital,
            taux: creditData.taux,
            duree_annees: creditData.dureeAnnees,
            date_debut: creditData.dateDebut.toISOString().split("T")[0],
          })
          .eq("id", editingCredit.id);

        if (error) console.error("Erreur update Supabase:", error);
      }
      setEditingCredit(null);
    }
  };

  const handleDeleteCredit = async (id: string) => {
    setCredits(credits.filter((c) => c.id !== id));

    if (user && isPremium) {
      const { error } = await supabase.from("credits").delete().eq("id", id);
      if (error) console.error("Erreur delete Supabase:", error);
    }
  };

  const openEditModal = (credit: Credit) => {
    setEditingCredit(credit);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCredit(null);
  };

  const totalMensualites = useMemo(() => {
    return credits.reduce((sum, c) => {
      const tableau = genererTableauAmortissement(c);
      const dateReference = new Date(); // Aujourd'hui
      const dateDebut = new Date(c.dateDebut);
      dateDebut.setDate(1);
      
      const refIndex = dateReference.getFullYear() * 12 + dateReference.getMonth();
      const debutIndex = dateDebut.getFullYear() * 12 + dateDebut.getMonth();
      const moisEcoules = refIndex - debutIndex + 1;
      const indexMoisActuel = Math.max(0, Math.min(moisEcoules - 1, tableau.length - 1));
      
      const mensualiteActuelle = tableau[indexMoisActuel]?.mensualite || 0;
      return sum + mensualiteActuelle;
    }, 0);
  }, [credits]);
  
  const tableauConsolide = useMemo(
    () => consoliderTableaux(credits),
    [credits]
  );
  const totalInteretsRestants = useMemo(
    () => calculerInteretsRestantsAujourdhui(credits),
    [credits]
  );
  const capitalRestantTotal = useMemo(
    () => calculerCapitalRestantTotalAujourdhui(credits),
    [credits]
  );
  const capitalTotalEmprunte = useMemo(
    () => credits.reduce((sum, c) => sum + c.capital, 0),
    [credits]
  );
  const capitalTotalRembourse = useMemo(
    () =>
      credits.reduce(
        (sum, c) => sum + (c.capital - calculerCapitalRestantAujourdhui(c)),
        0
      ),
    [credits]
  );
  const interetsTotaux = useMemo(() => {
    return credits.reduce((sum, c) => {
      const tableau = genererTableauAmortissement(c);
      const totalCredit = tableau.length > 0 ? tableau[tableau.length - 1].interetsCumules : 0;
      return sum + totalCredit;
    }, 0);
  }, [credits]);
  
  const interetsDejaPayes = useMemo(
    () => interetsTotaux - totalInteretsRestants,
    [interetsTotaux, totalInteretsRestants]
  );
  const pourcentageCapitalRembourse =
    capitalTotalEmprunte > 0
      ? ((capitalTotalRembourse / capitalTotalEmprunte) * 100).toFixed(1)
      : 0;
  const pourcentageInteretsPayes =
    interetsTotaux > 0
      ? ((interetsDejaPayes / interetsTotaux) * 100).toFixed(1)
      : 0;
  const pourcentageCapitalRestant = capitalTotalEmprunte > 0
      ? ((capitalRestantTotal / capitalTotalEmprunte) * 100).toFixed(1)
      : 0;
  const pourcentageInteretsRestants = interetsTotaux > 0
      ? ((totalInteretsRestants / interetsTotaux) * 100).toFixed(1)
      : 0;

  // ROUTAGE DES VUES
  if (viewMode === "renegociation")
    return (
      <div style={styles.container}>
        <div style={{ maxWidth: "80rem", margin: "0 auto" }}>
          <RenegociationView
            credits={credits}
            onBack={() => setViewMode("dashboard")}
          />
        </div>
      </div>
    );
  if (viewMode === "regroupement")
    return (
      <div style={styles.container}>
        <div style={{ maxWidth: "80rem", margin: "0 auto" }}>
          <RegroupementView
            credits={credits}
            onBack={() => setViewMode("dashboard")}
          />
        </div>
      </div>
    );
  if (viewMode === "remboursement")
    return (
      <div style={styles.container}>
        <div style={{ maxWidth: "80rem", margin: "0 auto" }}>
          <RemboursementView
            credits={credits}
            onBack={() => setViewMode("dashboard")}
          />
        </div>
      </div>
    );

  // ==========================================
  // VUE PROFIL (MON COMPTE)
  // ==========================================
  if (viewMode === "profile") {
    return (
      <ProfilePage 
        user={user} 
        isPremium={isPremium} 
        onClose={() => setViewMode("dashboard")} 
      />
    );
  }

  // ==========================================
  // VUE PRINCIPALE : DASHBOARD
  // ==========================================
  return (
    <div style={styles.container}>
      <div style={{ maxWidth: "80rem", margin: "0 auto", paddingBottom: "3rem" }}>
        
        {/* SECTION 1 : EN-TÊTE */}
        <section style={{ ...styles.header, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "1rem", marginBottom: "2rem" }}>
          <div>
            <h1 style={{ ...styles.title, fontSize: window.innerWidth < 768 ? "1.5rem" : "2rem" }}>
              Iaora Crédit
            </h1>
            <p style={styles.subtitle}>
              Un outil exclusif pour optimiser vos crédits
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {user ? (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button onClick={() => setViewMode("profile")} style={{ ...styles.button, backgroundColor: "#eff6ff", color: "#2563eb", border: "1px solid #dbeafe", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <User size={18} />
                  <span>Mon Compte</span>
                </button>
                <button onClick={() => supabase.auth.signOut()} style={{ ...styles.button, backgroundColor: "#f3f4f6", color: "#374151", border: "none" }}>
                  Déconnexion
                </button>
              </div>
            ) : (
              // NOUVEAU BOUTON PREMIUM MODERNISÉ
              <button 
                onClick={() => {
                  setIsUpgradeModalOpen(true);
                  Fathom.trackEvent('Clic Premium'); // Event envoyé à Fathom
                }}
                style={{ 
                  ...styles.button, 
                  background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)", // Dégradé orange
                  color: "white", 
                  border: "none", 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "0.5rem", 
                  padding: "0.5rem 1.2rem", 
                  fontWeight: "bold", 
                  borderRadius: "2rem", // Bords très arrondis
                  boxShadow: "0 4px 10px rgba(245, 158, 11, 0.3)" // Légère ombre brillante
                }}
              >
                <Crown size={18} color="white" />
                <span>Premium</span>
              </button>
            )}
          </div>
        </section>

        {/* ========================================== */}
        {/* SECTION 2 : MES CRÉDITS (OU AJOUT)         */}
        {/* ========================================== */}
        <section style={{ marginBottom: "2.5rem" }}>
          {credits.length > 0 ? (
            <div>
              <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
                <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#1f2937", margin: 0 }}>
                  Mes crédits
                </h2>
                
                {/* PETIT BOUTON : Affiché UNIQUEMENT sur Mobile */}
                {isMobile && (
                  <button 
                    onClick={() => { setEditingCredit(null); setIsModalOpen(true); }} 
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      padding: "0.3rem 0.75rem",
                      backgroundColor: "transparent",
                      color: "#2563eb",
                      border: "1px solid #bfdbfe",
                      borderRadius: "2rem",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    <PlusCircle size={16} />
                    <span>Ajouter un crédit</span>
                  </button>
                )}
              </div>
              
              <div style={styles.creditGrid}>
                {/* Liste des cartes existantes */}
                {credits.map((credit) => (
                  <CreditCard key={credit.id} credit={credit} onDelete={handleDeleteCredit} onEdit={openEditModal} />
                ))}

                {/* GRANDE CARTE : Affichée UNIQUEMENT sur Ordinateur */}
                {!isMobile && (
                  <div
                    onClick={() => { setEditingCredit(null); setIsModalOpen(true); }}
                    style={{
                      backgroundColor: "#f8fafc", // Gris très très clair
                      borderRadius: "1rem",
                      border: "2px dashed #cbd5e1", // Bordure en pointillés
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "2rem",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      color: "#94a3b8",
                      minHeight: "100%", // S'adapte à la hauteur des autres cartes
                    }}
                    // Effets de survol (Hover)
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#3b82f6";
                      e.currentTarget.style.backgroundColor = "#eff6ff";
                      e.currentTarget.style.color = "#2563eb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#cbd5e1";
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                      e.currentTarget.style.color = "#94a3b8";
                    }}
                  >
                    <PlusCircle size={48} style={{ marginBottom: "1rem" }} />
                    <h3 style={{ fontSize: "1.2rem", fontWeight: "600", margin: 0 }}>Ajouter un crédit</h3>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#1f2937", marginBottom: "0.5rem" }}>
                Aucun crédit pour le moment
              </h3>
              <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
                Commencez par ajouter vos crédits existants
              </p>
              <button onClick={() => { setEditingCredit(null); setIsModalOpen(true); }} style={{ ...styles.button, ...styles.buttonPrimary, flex: "none", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                <PlusCircle size={20} />
                <span>Ajouter mon premier crédit</span>
              </button>
            </div>
          )}
        </section>

       {/* SECTION 3 : CARTES KPI */}
        <section style={{ marginBottom: "3rem" }}>
          
          {/* --- NOUVEAU : TITRE DE LA SECTION --- */}
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#1f2937",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
            Récap de tous mes crédits
          </h2>
          {/* ----------------------------------- */}

          <div style={styles.kpiGrid}>
            {/* KPI Capital - STANDARD (Couleur = Remboursé) */}
            <div style={{ ...styles.kpiCard, ...styles.kpiCardGreen }}>
              <div style={styles.kpiHeader}>
                <DollarSign style={{ color: "#16a34a" }} size={24} />
                <h3 style={styles.kpiTitle}>Capital total emprunté</h3>
              </div>
              <p
                style={{
                  ...styles.kpiValue,
                  fontSize: "1.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                {capitalTotalEmprunte.toLocaleString()} XPF
              </p>

              {/* GRAPHIQUE À BARRE CAPITAL */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "1.25rem",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "9999px",
                }}
              >
                {(() => {
                  // UTILISATION DE LA VARIABLE "REMBOURSÉ"
                  const val = Number(pourcentageCapitalRembourse);
                  const rem = 100 - val;
                  return (
                    <>
                      <div
                        style={{
                          width: `${val}%`,
                          backgroundColor: "#10b981",
                          height: "100%",
                          transition: "width 0.5s ease-in-out",
                          borderRadius: "9999px",
                        }}
                      />

                      {/* Texte Gauche (Vert - Remboursé) */}
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          zIndex: 10,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          left:
                            val > 12 ? `${val / 2}%` : `calc(${val}% + 0.5rem)`,
                          transform:
                            val > 12
                              ? "translate(-50%, -50%)"
                              : "translate(0, -50%)",
                          color: val > 12 ? "white" : "#10b981",
                        }}
                      >
                        {val.toFixed(1)}%
                      </span>

                      {/* Texte Droite (Gris - Restant) */}
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          zIndex: 10,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          left:
                            rem > 12
                              ? `${val + rem / 2}%`
                              : `calc(${val}% - 0.5rem)`,
                          transform:
                            rem > 12
                              ? "translate(-50%, -50%)"
                              : "translate(-100%, -50%)",
                          color: rem > 12 ? "#6b7280" : "#4b5563",
                        }}
                      >
                        {rem.toFixed(1)}%
                      </span>
                    </>
                  );
                })()}
              </div>

              <div
                style={{ ...styles.progressBarDetails, marginTop: "0.35rem" }}
              >
                <span style={{ color: "#10b981", fontWeight: "500" }}>
                  {capitalTotalRembourse.toLocaleString()} remboursé
                </span>
                <span style={{ color: "#6b7280", fontWeight: "500" }}>
                  {capitalRestantTotal.toLocaleString()} restant dû
                </span>
              </div>
            </div>

            {/* KPI Mensualités */}
            <div style={{ ...styles.kpiCard, ...styles.kpiCardBlue }}>
              <div style={styles.kpiHeader}>
                <Wallet style={{ color: "#2563eb" }} size={24} />
                <h3 style={styles.kpiTitle}>Total mensualités</h3>
              </div>
              <p style={{ ...styles.kpiValue, fontSize: "1.5rem" }}>
                {totalMensualites.toLocaleString()} XPF
              </p>

              {/* Affichage conditionnel des paliers s'il y a plus d'un crédit */}
              {credits.length > 1 && (
                <div style={{ marginTop: "1rem" }}>
                  <button
                    onClick={(e) => {
                      const container = e.currentTarget
                        .nextElementSibling as HTMLElement;
                      if (container) {
                        container.style.display =
                          container.style.display === "none" ? "block" : "none";
                        const icon = e.currentTarget.querySelector("svg");
                        if (icon)
                          icon.style.transform =
                            container.style.display === "none"
                              ? "rotate(0deg)"
                              : "rotate(90deg)";
                      }
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#2563eb",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      padding: "0",
                    }}
                  >
                    Voir l'évolution future
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transition: "transform 0.2s" }}
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>

                  {/* Le conteneur des paliers */}
                  <div
                    style={{
                      display: "none",
                      marginTop: "0.75rem",
                      borderTop: "1px dashed #bfdbfe",
                      paddingTop: "0.75rem",
                    }}
                  >
                    {(() => {
                      const paliers = calculerPaliersMensualites(credits);

                      return paliers.map((palier, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: "0.85rem",
                            marginBottom: "0.5rem",
                            color: index === 0 ? "#1f2937" : "#6b7280",
                            fontWeight: index === 0 ? "600" : "normal",
                          }}
                        >
                          {/* 1. La mensualité à gauche */}
                          <span>{palier.mensualite.toLocaleString()} XPF</span>

                          {/* 2. La période à droite, plus discrète */}
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: index === 0 ? "#6b7280" : "#9ca3af",
                              backgroundColor:
                                index === 0 ? "#f3f4f6" : "transparent",
                              padding: index === 0 ? "0.15rem 0.4rem" : "0",
                              borderRadius: "0.25rem",
                            }}
                          >
                            {palier.periode}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* KPI Intérêts - STANDARD (Couleur = Payés) */}
            <div style={{ ...styles.kpiCard, ...styles.kpiCardOrange }}>
              <div style={styles.kpiHeader}>
                <TrendingDown style={{ color: "#ea580c" }} size={24} />
                <h3 style={styles.kpiTitle}>Total intérêts</h3>
              </div>
              <p
                style={{
                  ...styles.kpiValue,
                  fontSize: "1.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                {interetsTotaux.toLocaleString()} XPF
              </p>

              {/* GRAPHIQUE À BARRE INTÉRÊTS */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "1.25rem",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "9999px",
                }}
              >
                {(() => {
                  // UTILISATION DE LA VARIABLE "PAYÉS"
                  const val = Number(pourcentageInteretsPayes);
                  const rem = 100 - val;
                  return (
                    <>
                      <div
                        style={{
                          width: `${val}%`,
                          backgroundColor: "#f59e0b",
                          height: "100%",
                          transition: "width 0.5s ease-in-out",
                          borderRadius: "9999px",
                        }}
                      />

                      {/* Texte Gauche (Orange - Payés) */}
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          zIndex: 10,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          left:
                            val > 12 ? `${val / 2}%` : `calc(${val}% + 0.5rem)`,
                          transform:
                            val > 12
                              ? "translate(-50%, -50%)"
                              : "translate(0, -50%)",
                          color: val > 12 ? "white" : "#f59e0b",
                        }}
                      >
                        {val.toFixed(1)}%
                      </span>

                      {/* Texte Droite (Gris - Restants) */}
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          zIndex: 10,
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          left:
                            rem > 12
                              ? `${val + rem / 2}%`
                              : `calc(${val}% - 0.5rem)`,
                          transform:
                            rem > 12
                              ? "translate(-50%, -50%)"
                              : "translate(-100%, -50%)",
                          color: rem > 12 ? "#6b7280" : "#4b5563",
                        }}
                      >
                        {rem.toFixed(1)}%
                      </span>
                    </>
                  );
                })()}
              </div>

              <div
                style={{ ...styles.progressBarDetails, marginTop: "0.35rem" }}
              >
                <span style={{ color: "#f59e0b", fontWeight: "500" }}>
                  {interetsDejaPayes.toLocaleString()} payés
                </span>
                <span style={{ color: "#6b7280", fontWeight: "500" }}>
                  {totalInteretsRestants.toLocaleString()} restants
                </span>
              </div>
            </div>
          </div>
        </section>
       
        {/* SECTION 4 & 5 : OPTIMISATION ET TABLEAU */}
        {credits.length > 0 && (
          <>
            <section style={{ marginBottom: "3rem" }}>
              <OptimizeSection onSelectMode={setViewMode} />
            </section>
            
            {tableauConsolide.length > 0 && (
              <section style={{ marginBottom: "2rem" }}>
                <AmortizationTable credits={credits} />
              </section>
            )}
          </>
        )}

        <Footer /> 

        {/* --- MODALS --- */}
        {/* MODALS */}
        <CreditModal isOpen={isModalOpen} onClose={closeModal} onSave={editingCredit ? handleEditCredit : handleAddCredit} existingCredit={editingCredit} title={editingCredit ? "Modifier le crédit" : "Ajouter un crédit"} />
        <LimitModal isOpen={isLimitModalOpen} onClose={() => setIsLimitModalOpen(false)} onUpgrade={() => setIsUpgradeModalOpen(true)} />
        <UpgradeModal 
          isOpen={isUpgradeModalOpen} 
          onClose={() => setIsUpgradeModalOpen(false)} 
          setIsAuthModalOpen={setIsAuthModalOpen} 
          setAuthMode={setAuthMode}
          user={user} /* <--- NOUVELLE LIGNE À AJOUTER */
        />
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={() => setIsAuthModalOpen(false)} 
          mode={authMode} 
          setMode={setAuthMode}
          onOpenUpgrade={() => setIsUpgradeModalOpen(true)} /* <--- NOUVEAU */
        />

        {/* NOUVEAU : LE TRACEUR VERCEL EST ICI */}
        <Analytics />
      </div>
    </div>
  );
};

// ==========================================
// COMPOSANTS MODALS SÉPARÉS
// ==========================================
const LimitModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}> = ({ isOpen, onClose, onUpgrade }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "1rem",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "2rem",
          borderRadius: "1rem",
          maxWidth: "400px",
          width: "100%",
          textAlign: "center",
          position: "relative",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            background: "none",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
          }}
        >
          <X size={20} />
        </button>

        <div style={{ color: "#d93616", marginBottom: "1rem" }}>
          <AlertCircle size={48} style={{ margin: "0 auto" }} />
        </div>

        <h3
          style={{
            fontSize: "1.25rem",
            fontWeight: "bold",
            marginBottom: "1rem",
            color: "#1f2937",
          }}
        >
          Limite atteinte
        </h3>

        <p
          style={{
            color: "#4b5563",
            marginBottom: "1.5rem",
            fontSize: "0.95rem",
            lineHeight: "1.5",
          }}
        >
          Le plan gratuit est limité à <strong>1 seul crédit</strong>. 
          <br /><br />
          Passez au plan Premium pour ajouter des crédits en illimité et enregistrer automatiquement vos crédits pour éviter de les resaisir
        </p>

        <button
          onClick={() => {
            onClose();
            onUpgrade(); // Cela ouvrira la fenêtre Espace Premium
            Fathom.trackEvent('Clic Premium'); // Event envoyé à Fathom
          }}
          style={{
            background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)", // Bouton premium assorti
            color: "white",
            padding: "0.8rem",
            borderRadius: "0.5rem",
            border: "none",
            fontWeight: "bold",
            width: "100%",
            cursor: "pointer",
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <Crown size={18} /> Voir le plan Premium
        </button>

        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            fontSize: "0.85rem",
            cursor: "pointer",
            marginTop: "0.5rem",
          }}
        >
          Peut-être plus tard
        </button>
      </div>
    </div>
  );
};

const UpgradeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  setIsAuthModalOpen: (open: boolean) => void;
  setAuthMode: (mode: "login" | "signup") => void;
  user: any; // <--- NOUVEAU PARAMÈTRE
}> = ({ isOpen, onClose, setIsAuthModalOpen, setAuthMode, user }) => {
  if (!isOpen) return null;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "1rem" }}>
      <div style={{ backgroundColor: "white", padding: "2rem", borderRadius: "1.5rem", maxWidth: "600px", width: "100%", textAlign: "center", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", position: "relative", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: "1.5rem", right: "1.5rem", background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: "0.5rem" }}>
          <X size={28} />
        </button>

        <div style={{ backgroundColor: "#fff7ed", width: "80px", height: "80px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem", border: "3px solid #f59e0b" }}>
          <Crown size={40} color="#f59e0b" fill="#f59e0b" fillOpacity={0.2} />
        </div>

        <h2 style={{ fontSize: "2rem", fontWeight: "800", marginBottom: "2rem", color: "#1f2937" }}>
          Plan Premium
        </h2>

        <div style={{ marginBottom: "2.5rem", padding: "2rem", border: "2px solid #f59e0b", borderRadius: "1rem", backgroundColor: "#fffbeb" }}>
          <p style={{ fontWeight: "bold", marginBottom: "1.5rem", color: "#92400e", fontSize: "1.1rem" }}>
            Avantages
          </p>
          <ul style={{ textAlign: "left", fontSize: "1rem", marginBottom: "2rem", paddingLeft: "0", listStyle: "none", color: "#4b5563" }}>
            <li style={{ marginBottom: "1rem", display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
              <span style={{ color: "#10b981", fontSize: "1.2rem" }}>✅</span><span>Aucune limite, ajoutez tous vos crédits pour avoir une vision globale de votre situation</span>
            </li>
            <li style={{ marginBottom: "1rem", display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
               <span style={{ color: "#10b981", fontSize: "1.2rem" }}>✅</span><span>Vos crédits enregistrés sur votre espace membre pour éviter de les resaisir</span>
            </li>
          </ul>
          
          <button
            onClick={() => {
              if (!user) {
                // S'il n'a pas de compte, on ferme ici et on ouvre la création de compte
                onClose();
                setIsAuthModalOpen(true);
                setAuthMode("signup"); 
              } else {
                // S'il est connecté, on ouvre Stripe AVEC son ID secret !
                window.open(`https://buy.stripe.com/eVq9ALgHV1Qa0E1cz93Nm01?client_reference_id=${user.id}`, "_blank");
              }
            }}
            style={{ backgroundColor: "#f59e0b", color: "white", padding: "1rem", borderRadius: "0.75rem", border: "none", fontWeight: "bold", fontSize: "1.1rem", width: "100%", cursor: "pointer", boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.4)" }}
          >
            {user ? "Continuer vers le paiement (9.900 FCP/an)" : "Devenir Premium pour 9.900 FCP par an"}
          </button>

          <p
            style={{
              fontSize: "0.9rem",
              textAlign: "center",
              marginTop: "1rem",
              color: "#92400e",
            }}
          >
            Vous pouvez annuler le renouvellement de votre abonnement à tout moment.
          </p>
        
          {/*<p
            style={{
              fontSize: "0.9rem",
              textAlign: "center",
              marginTop: "1rem",
              color: "#92400e",
            }}
          >
            <span style={{ fontWeight: "bold" }}>Tarif : 9.900 FCP / an </span>
          </p>

          <p
            style={{
              fontSize: "0.9rem",
              textAlign: "center",
              marginTop: "1rem",
              color: "#92400e",
            }}
          >
            <span style={{ fontWeight: "bold" }}>50% de réduction</span> pour
            les 30 premiers Premium, soit 4.950 FCP la 1ère année
          </p>
          <p style={{ fontWeight: "bold", color: "#92400e", textAlign: "center", fontSize:"1rem" }}>
            Code promo : <span style={{ backgroundColor: "#fef3c7", padding: "0.2rem 0.5rem", borderRadius: "0.25rem", border: "1px dashed #f59e0b" }}>IAORA50</span>
          </p>*/}
        </div>

        {/* Le bouton "Déjà membre / Se connecter" n'apparaît que s'il n'est pas connecté */}
        {!user && (
          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "1.5rem" }}>
            <p style={{ fontSize: "1rem", color: "#4b5563", marginBottom: "1rem" }}>
              Déjà membre Premium ?
            </p>
            <button
              onClick={() => { onClose(); setIsAuthModalOpen(true); setAuthMode("login"); }}
              style={{ backgroundColor: "transparent", color: "#2563eb", padding: "0.8rem", borderRadius: "0.5rem", border: "2px solid #2563eb", fontWeight: "bold", fontSize: "1rem", width: "100%", cursor: "pointer" }}
            >
              Se connecter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AuthModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  mode: "login" | "signup";
  setMode: (mode: "login" | "signup") => void;
  onOpenUpgrade: () => void;
}> = ({ isOpen, onClose, mode, setMode, onOpenUpgrade }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Variables pour adapter le texte selon le mode
  const isLogin = mode === "login";
  const title = isLogin ? "Connexion Premium" : "Création de compte";
  const subtitle = isLogin 
    ? "Connectez-vous à votre espace membre pour retrouver vos crédits sauvegardés."
    : "Créez votre compte sécurisé avant de passer au paiement de votre abonnement.";

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!isLogin) {
      // MODE INSCRIPTION
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setError(error.message);
        setLoading(false);
      } else if (data?.user) {
        // 🎉 REDIRECTION DIRECTE VERS STRIPE
        // On n'affiche plus d'alerte et on ne réouvre pas la modale Premium.
        // On redirige instantanément l'utilisateur avec son nouvel ID sécurisé.
        window.location.href = `https://buy.stripe.com/eVq9ALgHV1Qa0E1cz93Nm01?client_reference_id=${data.user.id}`;
      }
    } else {
      // MODE CONNEXION
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError("Identifiants incorrects.");
      } else {
        onClose();
      }
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Veuillez saisir votre e-mail d'abord.");
      return;
    }
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });

    if (error) {
      setError(error.message);
    } else {
      alert("Un lien de réinitialisation a été envoyé sur votre boîte mail !");
    }
    setLoading(false);
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{title}</h2>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={24} />
          </button>
        </div>

        <p
          style={{
            fontSize: "0.9rem",
            color: "#6b7280",
            marginBottom: "1.5rem",
            lineHeight: "1.4",
          }}
        >
          {subtitle}
        </p>

        <form onSubmit={handleAuth} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="votre@email.com"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>

          {/* MOT DE PASSE OUBLIÉ (Affiché UNIQUEMENT en mode connexion) */}
          {isLogin && (
            <div
              style={{
                textAlign: "right",
                marginTop: "-10px",
                marginBottom: "15px",
              }}
            >
              <button
                type="button"
                onClick={handleResetPassword}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}

          {error && <p style={styles.errorText}>{error}</p>}

          {/* MENTION LÉGALE À L'INSCRIPTION */}
          {!isLogin && (
            <p style={{ fontSize: "0.75rem", color: "#6b7280", textAlign: "center", marginTop: "0.5rem", lineHeight: "1.5" }}>
              En cliquant sur "Créer mon compte", vous acceptez nos{" "}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("openLegalModal", { detail: "cgu" }))}
                style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", textDecoration: "underline", padding: 0, fontSize: "0.75rem", fontFamily: "inherit" }}
              >
                Conditions Générales d'Utilisation
              </button>
              {" "}et notre{" "}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("openLegalModal", { detail: "privacy" }))}
                style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", textDecoration: "underline", padding: 0, fontSize: "0.75rem", fontFamily: "inherit" }}
              >
                Politique de Confidentialité
              </button>.
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              marginBottom: "1rem",
            }}
          >
            {loading ? "Chargement..." : isLogin ? "Se connecter" : "Créer mon compte"}
          </button>

          {/* MESSAGE POUR BASCULER ENTRE LES MODES */}
          <div
            style={{
              marginTop: "1rem",
              paddingTop: "1rem",
              borderTop: "1px solid #f3f4f6",
              textAlign: "center",
            }}
          >
            {isLogin ? (
              <>
                <p style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.5rem" }}>
                  Pas encore membre Premium ?
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onClose(); 
                    onOpenUpgrade(); 
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#f59e0b",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  Créer mon compte Premium
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.5rem" }}>
                  Déjà membre Premium ?
                </p>
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#2563eb",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                  }}
                >
                  Se connecter
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default App;
